// src/config/featureFlags.js
//
// Centralized feature flags. Code constants — flip in a follow-up PR
// when the gated feature is ready to ship.

/**
 * Watch List rail on the Discover tab.
 *
 * Disabled while we build deterministic calendar infrastructure to
 * replace Sonar as the data source. See SPRINT_4_LESSONS.md for context
 * on what's deferred and the open architecture questions.
 */
export const WATCH_LIST_RAIL_ENABLED = false;

/**
 * Command Dashboard — the mobile loop-home (Read → Equip → Deploy → Manage → Review).
 *
 * When false, the mobile home renders the existing DashboardLoop unchanged
 * (instant rollback). When true, it renders the new agent-command surface.
 * Mobile only — desktop (DashboardDesktop) is unaffected either way.
 */
// Pinned by: activeNavigation.shelvedSurfaces.test.jsx (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const COMMAND_DASHBOARD_ENABLED = true;

/**
 * Command Dashboard — Desktop (the 3-column command surface that pairs with the
 * mobile loop-home: Read → Equip → Deploy → Manage → Review).
 *
 * When false, desktop renders the existing DashboardDesktop unchanged (instant
 * rollback). When true (and the viewport is desktop), it renders
 * CommandDashboardDesktop. Built/merged dark; flip to ship after a preview
 * smoke test — the same play used for the mobile flag above.
 */
// Pinned by: activeNavigation.shelvedSurfaces.test.jsx (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const COMMAND_DASHBOARD_DESKTOP_ENABLED = true;

/**
 * Forge — authored per-rule Hard/Soft override (Phase 3).
 *
 * When false (default), the bundle build flow's Hard/Soft stage renders the
 * read-only, category-derived badge — today's behavior, with no way to author
 * an override. When true, it renders the interactive per-rule SOFT/HARD control
 * that writes a `ruleHardness` override onto the bundle doc.
 *
 * GATED OFF until the FENCED prompt-assembly half lands (projectActiveRules
 * carrying `hardness`; agentPromptAssembly + agentEvalPromptAssembly honoring
 * the override) AND a founder sign-off on that fenced commit — so users never
 * get a "must follow" control the agent silently ignores. The non-fenced
 * authoring + persistence + display layer is built and merged dark behind this
 * flag; flip to ship only after the fenced commit is reviewed for prompt parity
 * and signed off. See FORGE_ENFORCEMENT_KEYSTONE_SPEC and the Phase 3 audit.
 */
export const FORGE_HARDSOFT_AUTHORING_ENABLED = true;

/**
 * Forge — Desktop layout for the Forge workshop shell (ForgeWorkshop).
 *
 * When false (default), ForgeWorkshop renders the existing fixed 480px centered
 * column at every viewport — instant rollback; mobile AND desktop unchanged.
 * When true (and the viewport is desktop), the shell widens and the `01 Lists`
 * area unfolds into the two-column Discover + create/manage layout. Built/merged
 * dark; flip to ship after a Vercel preview smoke test — the
 * COMMAND_DASHBOARD_DESKTOP_ENABLED precedent.
 */
export const FORGE_DESKTOP_ENABLED = true;

/**
 * Forge — Traits → Archetype Exploration surface (the `03 Traits` redesign).
 *
 * When false (default), the Traits area renders TODAY's interim surface unchanged
 * at every viewport (read-only archetype banner + DNAGroupCard/TraitCard equip on
 * mobile, the EquippedTraitCard grid + workbench banner on desktop) — instant
 * rollback. When true (or via the `?traitsExploration=1` dev-preview param, the
 * `?forgeDesktop=1` idiom), the area renders the exploration redesign: archetypes
 * as explorable characters (identity + four decision factors), the live equipped
 * loadout with honest per-rule hardness, and a view-only six-archetype roster +
 * trait library.
 *
 * This changes the LIVE mobile Traits surface (not gated by FORGE_DESKTOP_ENABLED),
 * so it is built/merged DARK behind this flag on BOTH viewports. Flip to ship in a
 * one-line follow-up PR after a Vercel preview smoke test — the
 * COMMAND_DASHBOARD_DESKTOP_ENABLED / LEAGUE_REDESIGN_ENABLED precedent.
 */
export const TRAITS_EXPLORATION_ENABLED = true;

/**
 * Release 3 — the Forge's `03 Traits` tab BECOMES `03 Character` (Character/Equip
 * UI). When true (or via the `?release3Character=1` dev-preview param, the
 * `?traitsExploration=1` idiom), the `03` slot's nav label reads "Character" and
 * its body renders the CharacterArea: the archetype identity reading (kept), a
 * read-only "Born with" kit, standing leans (equip/unequip, 2 slots), the tempo
 * dial, and a derived behavior fingerprint — the interactive trait library /
 * SOFT NUDGE / STRENGTH selectors are removed from this surface. The Forge
 * landing "Traits" card likewise becomes a "Character" card.
 *
 * When FALSE (default), the `03` slot renders TODAY's Traits surface byte-for-byte
 * (label "Traits" → TraitsArea → TraitsExploration) — the instant-rollback
 * guarantee (off-state proof). It consumes the already-merged Release 2 lean/dial
 * contracts (equip-lean / unequip-lean / set-tempo-dial), which are LIVE as of
 * 2026-07-12; this flag gates only the UI. Flip to ship in a one-line follow-up
 * PR after a Vercel preview smoke test — the TRAITS_EXPLORATION_ENABLED precedent.
 */
export const RELEASE3_CHARACTER_TAB_ENABLED = true;

/**
 * Equip bench — the Traits loadout slot (mobile EquipStation + desktop
 * EquipBench) and the TraitsSheet behind it.
 *
 * OFF at launch: the fixed-library trait surface is retired (Scouting Focus
 * Build Spec V1.3 §11 — trait surface off, plumbing seeded silently; Closeout
 * Spec V1.1 §2). When false, both benches render 2 slots (Archetype ·
 * Watchlist) and the slot-count copy follows the rendered slots. Surface-only:
 * equippedTraits seeding, trait persistence, and the projectActiveRules
 * projection are untouched — agents keep their seeded traits invisibly and
 * battle behavior is unchanged. Flip when the slot returns post-launch as the
 * earned-trait perk slot.
 */
export const TRAIT_SLOT_ENABLED = false;

/**
 * League — the provisional tournament tab occupying the retired Agent Hub's
 * nav slot (Closeout Spec V1.1 §6).
 *
 * When false, the slot is hidden entirely (4-item bottom nav, 6-item desktop
 * sidebar) and the 'league' route is gated away — the screen id is unreachable
 * since the nav items are its only setters. When true, a provisional "League"
 * tab (Trophy icon) renders the placeholder LeagueScreen. Flip when the League
 * Tournament surface is real.
 */
export const TOURNAMENT_TAB_ENABLED = true;

/**
 * League — the self-serve lobby (P10): registration · join/create · FIFO
 * matchmaking · CPU-padded formation. The front door that replaces the dead
 * "no active tournament group yet" empty state on the live League tab.
 *
 * When false (default), the League tab renders TODAY's behavior unchanged —
 * `subscribeMyGroup` and the existing forming→battle flow are untouched, and
 * the `!group` branch shows the coming-soon poster (dark-merge safe; the live
 * tournament must not regress). When true, the empty state becomes the lobby
 * front door and the lobby-* endpoints accept self-serve registration.
 *
 * Built/merged DARK behind this flag (P10a = the data layer + the proven
 * CPU-padding-from-base-layer seam; P10b = the surface). Flip — a one-line
 * follow-up PR, the TOURNAMENT_TAB_ENABLED precedent — only after a preview
 * smoke + the new firestore.rules `tournamentLobby` block is deployed in the
 * Firebase Console (else the client lobby read 403s).
 */
export const LEAGUE_LOBBY_ENABLED = true;

/**
 * League — the redesigned spectate-and-enter front end (the bracket-funnel
 * lobby + four-player pod cards + pod sheet + two-layer Spectate). The
 * "front door" landing that sits in front of the live participant flow
 * (LeagueScreen's board-commit / battle / claims / draft views stay intact
 * and are reached from it via a full-screen push).
 *
 * When false (default), the League tab renders TODAY's behavior unchanged —
 * LeagueScreen mounts LeagueParticipantView (the extracted, byte-identical
 * existing flow). The redesign is built/merged DARK behind this flag, and is
 * fixtures-backed for now (a single `useLeagueState()` seam; real Firestore
 * wiring is a scoped follow-on). When true — or with the `?leagueRedesign=1`
 * dev preview (the `?tournamentDev=1` idiom) — the tab renders LeagueHome.
 *
 * Flip — a one-line follow-up PR, the TOURNAMENT_TAB_ENABLED precedent —
 * only after a Vercel preview smoke test.
 */
export const LEAGUE_REDESIGN_ENABLED = true;

/**
 * League — Next Arc (the second League slice). The "Altitude Climb" five-day
 * pod-standings view now; in later phases the Training/Ranked lobby tabs, the
 * real-data adapter, and the live-pulse. Built/merged DARK behind this flag.
 *
 * When false (default), NOTHING in the League surface reads it — the tab is
 * byte-unchanged. This slice ships the Altitude Climb fixtures-backed and
 * reachable ONLY via the dev preview param `?leagueClimb=1` (with
 * `&m=live|final&c=training|ranked`) — the `?leagueRedesign=1` idiom.
 *
 * Phase 1 (the real-data adapter) also gates on this flag: when true (or with
 * the dev preview param `?leagueRealData=1`, the same idiom), `useLeagueState`
 * swaps fixtures for the real System-1 read-model (your group, the capped
 * base-layer field, the bracket funnel, dailyScores composites, the WHY
 * projection); when false + no param, `useLeagueState` returns byte-identical
 * fixtures. Fixtures remain the cold-start fill.
 *
 * The flag exists so a later phase can wire the real in-app entry and flip it;
 * do NOT flip it in a build PR (the PR #510 lesson), only after a Vercel
 * preview smoke.
 */
export const LEAGUE_NEXT_ARC_ENABLED = true;

/**
 * League — Battle View V2 (the redesigned live battle hero + nine-star command
 * dock). The locked Claude Design rebuild that sits in front of today's
 * Flat6BattleView / Altitude-Climb composition, EXTENDING the existing climb
 * (LEAGUE_NEXT_ARC) rather than forking it.
 *
 * When false (default), NOTHING in the League surface reads it — the tab is
 * byte-unchanged. Phase 1 (this slice) ships the PURE data foundation only (the
 * dailyScores→scores[] climb assembler, the per-holding meter reader + star
 * state, points formatters, beat derivation) with NO UI swap and NO read-site,
 * so flag-off is provably byte-identical. The dev preview `?battleViewV2=1` (the
 * `?leagueClimb=1` / `?leagueRedesign=1` idiom) and the LeagueScreen read-site
 * land with the component phase, not here.
 *
 * The flag exists so a later phase can wire the in-app entry and flip it; do NOT
 * flip it in a build PR (the PR #510 lesson — the LEAGUE_NEXT_ARC_ENABLED
 * precedent), only after a Vercel preview smoke.
 */
export const LEAGUE_BATTLE_VIEW_V2_ENABLED = true;

/**
 * Per-Battle Loadout + Per-Type Concurrency — Phase 1 (the casual clone).
 * Design lock: docs/../20260805_PER_BATTLE_LOADOUT_CONCURRENCY_DESIGN_LOCK_V1.
 *
 * When TRUE, a Command-Center BaggerBomb deploy runs on a PERSISTENT per-user
 * "casual clone" agent (agents/casual-agent-{odUserId}) — a behavioral clone of
 * the player's ranked agent with its OWN agentId — instead of the real ranked
 * agent. Because the one-active-battle lock in decide.js is agentId-scoped, the
 * clone delivers "one BaggerBomb at a time" for free AND lets BaggerBomb run
 * CONCURRENTLY with a ranked league game, with ZERO fenced edits (the
 * training-clone precedent). UNLIKE training (which isolates), a casual battle is
 * the player's REAL game, so its record + learning are REDIRECTED forward to the
 * parent ranked agent at the settlement/learning layer (attribution redirects),
 * preserving exactly what BaggerBomb contributes today.
 *
 * When FALSE (default), NOTHING creates a casual clone: agentDeploy deploys the
 * real ranked agent exactly as today, no battle ever carries a `casual-agent-`
 * agentId, and every attribution-redirect + ranked-lookup-exclusion branch (all
 * keyed on that prefix) is inert — so flag-off is byte-identical.
 *
 * The flag gates CREATION only (agentDeploy); the redirects key on the id prefix
 * so a mid-pilot rollback still attributes existing casual battles correctly. Do
 * NOT flip it in a build PR (the PR #510 lesson) — only after a Vercel preview.
 */
export const CASUAL_CLONE_CONCURRENCY_ENABLED = true;

/**
 * League Battleview Routing (Spec V1.2, Phase A) — the Command Center live-game
 * card path. A tapped game that is a flat-6 LEAGUE battle
 * (gameMode === 'baggerbomb_tournament') routes to the League Arena instead of
 * the BaggerBomb AgentBattleScreen (src/screens/BattleViewScreen.jsx).
 *
 * ON. Shipped true; it is NOT a dark flag any more. With it on, no League battle reaches
 * AgentBattleScreen, so none reaches the cockpit or POST /api/agent/call-response.
 *
 * THE ONE WALL (contract Amendment E, docs/CALL_RECORD_FIELD_CONTRACT_V1_4_AMENDMENT_E.md,
 * "What stays as it is"): this flag is the ONLY thing keeping League battles out of
 * the cockpit. In a League battle the agent's exit-call `slot` is a GUESS — the League
 * prompt shows no tiers, yet the declaration requires one — so the cockpit's slot-tier
 * answer check (D-4) and the any-tier checks of E-1 / E-2 disagree on 47% of those
 * calls (60 of 127 exit calls at the count). DO NOT TURN THIS FLAG OFF until the cockpit
 * arc's League SLOT LABEL follow-up has landed. Off, League battles fall through to
 * the agentDeployed → AgentBattleScreen branch again ("the mis-route",
 * BattleViewScreen.routing.dark.test.jsx) and into the cockpit.
 *
 * The `?leagueRouting=1` preview override of the dark build is gone; the expression
 * statement under the declaration is its inert leftover and does nothing.
 *
 * Pinned by: leagueBattleviewRoutingWall.test.js — a tripwire whose failure message
 * names Amendment E and the slot-label follow-up, and which also fails if this
 * docstring stops naming them.
 */
export const LEAGUE_BATTLEVIEW_ROUTING_ENABLED = true;
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('leagueRouting') === '1';

/**
 * League Battleview LIVE ORB (Phase B, Option X) — the whole live all-seats orb as
 * ONE unit. When ON, the arena climb goes live for EVERY seat this tick:
 *   • YOUR seat — the per-tick client path (youLiveScore) is allowed in RANKED too
 *     (not only training), keeping every other #572 guard (BATTLE, activation-day,
 *     not-yet-banked, real battle);
 *   • RIVALS — their live composite is sourced from the read-only endpoint
 *     (GET /api/tournament/live-composites), the only place a rival's owner-scoped
 *     agent six can be summed (B1 hard-stop). YOUR seat is NEVER routed through the
 *     endpoint (Option X): it stays on youLiveScore.
 * The two halves flip together so the climb is never your-live-vs-rivals-banked.
 *
 * Default OFF (dark): no URL param → false → the orb is exactly today (training
 * keeps its existing your-seat live orb; ranked stays banked; rivals banked; no
 * endpoint poll). Enabling is the only behavior change — byte-identical when off.
 *
 * PREVIEW SMOKE OVERRIDE: `?leagueLiveOrb=1` force-enables it in a Vercel preview
 * WITHOUT flipping the shipped default (the arenaLiveGate.js `?battleArenaLive=1`
 * idiom). Smoke alongside routing: `?leagueRouting=1&leagueLiveOrb=1`. The
 * production flip stays a separate one-line PR after smoke (the PR #510 precedent).
 */
export const LEAGUE_LIVE_ORB_ENABLED = true;
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('leagueLiveOrb') === '1';


/**
 * League — the FUSE HERO (Branch A): the battleview's top half becomes a fuse
 * board (x is the CLOCK, y is POINTS; the four seats render as glowing lines
 * with a burning tip carrying the seat's mech head and its running value) in
 * place of the points-axis `ClimbArena` scatter. Authority:
 * LEAGUE_BATTLEVIEW_ADJUDICATION_V1 (R1-R14) + the Branch A Build Spec and its
 * Amendment A (Phase 0.5 gate: G1 CLEAR, G2 client-derived cut, G3 confirmed).
 *
 * LIVE. The fuse hero is the top half of the League battle arena; ArenaDesktop
 * and ArenaMobile branch ONLY that top half on this flag, so `ClimbArena` is
 * still mounted and still correct — it is the ROLLBACK PATH, and it stays for a
 * couple of weeks while the fuse holds. Setting this back to `false` restores
 * the previous board exactly, with no other change required.
 *
 * FLIPPED in its own one-line PR (the LEAGUE_BATTLE_VIEW_V2_ENABLED / PR #510
 * precedent), over a weekend BETWEEN tournaments — a League runs Monday to
 * Friday and the board must not change under players mid-competition. The
 * `?fuseHero=1` preview override was deleted in that same commit: flip, pin and
 * override travel together (the ?leagueLiveOrb=1 lesson — a retired override
 * left behind becomes a dangling no-op nobody dares remove).
 *
 * `ClimbArena` removal is a THIRD, later PR, not this one.
 *
 * CLEANUP-PR OBLIGATIONS (Amendment B §B3, extended F4 — recorded here so they
 * survive the months until that PR). THREE items will look like dead weight for
 * a deleted component to whoever does the cleanup. None of them is:
 *   - b3Lockstep.test.js pins the seatAltitude call SHAPE at both resolution
 *     sites; before ClimbArena is deleted it needs a FuseHero counterpart (or
 *     an equivalent basis-invariant test) — never plain deletion.
 *   - the seatHasLiveSample truth table in seatAltitude.test.js pins the
 *     sampling predicate to the resolver's own branches; it guards the session
 *     trail (and therefore the B2 cut), not ClimbArena, and stays.
 *   - battleArena/arenaAtmosphere.jsx is SHARED by ClimbArena and FuseHero
 *     (F4 extraction). Deleting ClimbArena must NOT take it with them — the
 *     fuse is its other consumer.
 *
 * Pinned by: leagueBattleviewFlags.test.js, fuseHeroGate.test.jsx (flagPinGuard: this value and the pins move together — BUILD_RULES §2; the gate suite's dark-default rows flip with the pin).
 */
export const LEAGUE_FUSE_HERO_ENABLED = true;
/**
 * League — Training-tab CLIMB PREVIEW (the second-arc re-entry surface).
 *
 * The active-training-battle state of the League Training tab shows the real
 * five-day Altitude Climb (the Battle-View-V2 hero, `ClimbArena`, fed by the
 * real-data bridge `buildArenaModel` → `buildClimbSeries`) in place of the flat
 * "Return to your training pod" re-entry card; tapping the climb routes into the
 * pod's live battle view via the already-threaded `onOpenTrainingPod`.
 *
 * Reuses the real-data arena path so it renders the ACTUAL pod (never the
 * fixtures-only standalone `LeagueClimb`, which would leak the demo pod onto a
 * real surface). Scoped to `GROUP_STATUS.BATTLE` pods only — a DRAFTING /
 * AWAITING_OPEN pod has no climb yet and keeps its existing re-entry card.
 *
 * When false, NOTHING on the Training tab reads the real-data climb — the
 * re-entry card renders exactly as today (flag-off is byte-unchanged), and the
 * surface stays reachable via the dev param `?trainingClimbPreview=1` (the
 * `?leagueClimb=1` / `?battleViewV2=1` idiom).
 *
 * ENABLED (founder decision, 2026-07-03): flipped ON in the enabling PR rather
 * than a separate follow-on — the PR's own Vercel preview (feature ON) is the
 * smoke surface, and the founder merges manually only after smoking it. Because
 * the read-site swaps a production surface, the climb goes live at merge; smoke
 * the preview (a BATTLE training pod → the Training tab shows the real climb,
 * tap → the battle view) before merging.
 */
export const LEAGUE_TRAINING_CLIMB_PREVIEW_ENABLED = true;

/**
 * League — user-layer CANONICAL-OPEN baseline capture.
 *
 * When false (default), the user 3-pick layer keeps today's behavior: each
 * leg baseline is created null and settled at the next open by the nightly
 * banking pass (Spec §1.1), so held picks read 0× intraday on Day-0.
 *
 * When true, a League round is stamped `baselinePolicy: 'canonical_open'` at
 * creation, and a post-open capture sweep (a later phase) settles each leg's
 * baseline to the round's canonical session open — sourced from the SAME
 * `fetchBatchQuotes(...).open` (EODHD /real-time/ item.open) that banking
 * settles from — so the user layer scores live intraday, consistent with the
 * banked score of record.
 *
 * Phase 1 ships the INERT primitives only (this flag, the capture util, the
 * snapshot-storage shape + policy stamp, the leg provenance fields). Nothing
 * reads the stamp or the fields yet, so flag-off is behavior-identical. The
 * cron sweep + banking consumption land in later phases. Do NOT flip in a
 * build PR (the PR #510 / LEAGUE_NEXT_ARC_ENABLED precedent) — flip only after
 * a Vercel preview smoke.
 */
export const LEAGUE_CANONICAL_OPEN_CAPTURE = true;

/**
 * League — SCORE HISTORY / recap (Level 1 per-day composite timeline + swap
 * history) in the Film Room.
 *
 * The shipped decomposition strip answers "how is TODAY built" but (a) leaves
 * `BANKED` a single opaque aggregate, and (b) vanishes at the bank (the live
 * path is gated on `!dayBanked`). This flag lights the across-the-battle half:
 * the Film Room (the complete-state "break the seal" overlay, until now an empty
 * placeholder) fills with the per-day composite timeline and the per-day swap
 * ledger, and becomes reachable BOTH during a live battle (a "week so far"
 * entry) and — via a dedicated most-recent-COMPLETED group read that mirrors the
 * voided-card read — after the bank, so the recap survives completion.
 *
 * Every number shown is already persisted or re-derivable (NO new persistence):
 * Level 1 is a pure read of `dailyScores.dayN.closeScores[uid].compositePoints`
 * (buildClimbSeries); swaps are the per-day `agentBattles.trades[]` chain the
 * client already fetches then discards (pickCurrentTournamentBattle). Swap
 * points reconcile with the live strip's SWAPS term BY CONSTRUCTION — both
 * derive from `buildSwapLedger` (BUILD_RULES §9). Per-symbol agent BASE for
 * prior days is NOT persisted (aggregate only) and is labelled unavailable,
 * never approximated.
 *
 * When false (DEFAULT, merge-dark): the Film Room keeps its placeholder, no
 * completed-group read is subscribed, `useMyTournamentBattle`'s chain is unread,
 * and nothing new renders on any surface — flag-off is byte-identical.
 *
 * PREVIEW SMOKE OVERRIDE: `?leagueScoreHistory=1` force-enables it in a Vercel
 * preview WITHOUT flipping the shipped default (the `?leagueLiveOrb=1` /
 * `?battleArenaLive=1` idiom). The production flip stays a separate one-line PR
 * after the founder smokes the preview (the PR #510 precedent).
 */
export const LEAGUE_SCORE_HISTORY_ENABLED = true;

// The resolved gate consumers read (the ARENA_LIVE_ON idiom): the shipped
// default OR the preview param. Evaluated once at module load. Never pin THIS —
// pin LEAGUE_SCORE_HISTORY_ENABLED (the literal) if a suite must assert the dark
// default.
export const LEAGUE_SCORE_HISTORY_ON =
  LEAGUE_SCORE_HISTORY_ENABLED ||
  (typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('leagueScoreHistory') === '1');

/**
 * League — Competitive Live Draft (slot lobbies). The interactive live draft
 * as the competitive entry: a weekly schedule of draft slots (config-driven),
 * a user claims a seat, and a slot with ≥1 human at fire time drafts (CPU fills
 * the empty seats); a slot nobody claims never materializes.
 *
 * When false (DEFAULT, merge-dark), competitive formation behaves EXACTLY as
 * today: a group forms and is resolved single-shot at the Monday lock (the
 * runMondayPipeline FORMING path). The slot schedule is never consulted, the
 * slot-* endpoints 404 (the SCOUTING_BOARD defense-in-depth pattern), no slot
 * group is ever created, and nothing reads `scheduledDraftAt` / `battleStartWeek`
 * / `isLiveDraft` — so flag-off is byte-identical (the standing bar).
 *
 * When true, the slot picker (Phase 4) claims a seat via the slot-* endpoints;
 * the first claim lazily creates a FORMING slot group stamped with its fire
 * instant + Monday battle anchor; a dedicated every-10-minute fire cron (Phase 2)
 * CPU-fills and opens the interactive draft. The `status==='battle'` firewall is
 * untouched — a slot group is invisible to the sweep / banking / scorers until
 * the open, exactly like any other pre-battle group.
 *
 * Built/merged DARK behind this flag across Phases 1–5; flip to ship only after
 * the founder's preview smoke (a real slot claim → draft → battle) — never in a
 * build PR (the PR #510 / LEAGUE_NEXT_ARC_ENABLED precedent).
 */
export const LEAGUE_LIVE_DRAFT = true;

/**
 * League Training — the redesigned Training Draft Board (the agent-fit spine).
 *
 * When false (default), `TrainingDraftRoomScreen` renders TODAY's behavior
 * unchanged — the sector-grouped board + star-highlight overlay (the Slice 2
 * monolith), byte-identical (instant rollback). When true — or with the
 * `?trainingBoard=1` dev-preview param (the `?trainingDraft=` idiom) — it
 * renders the redesigned board: one fit-ranked, tiered "best available" board
 * keyed to the practice agent's archetype (`arch_scores[humanArchetype]`, a
 * direct read), plain-language reason lines, sector lens chips, search, and
 * scale handling, composed from the reusable League draft atoms in
 * `src/components/League/draft/`.
 *
 * Client UI + reads only — no new writes, the same `applyTrainingPick` endpoint,
 * the calibration fence untouched. Built/merged DARK behind this flag; flip in a
 * one-line follow-up PR after a Vercel preview smoke (the LEAGUE_REDESIGN_ENABLED
 * precedent). The opponent-reveal animation (Phase 2) and the entry-fold merge
 * (Phase 3) layer on behind this same flag.
 */
export const TRAINING_BOARD_REDESIGN_ENABLED = true;

/**
 * League Training — Training Pod Draft V2 (the Draft Lobby & Awaiting-Open
 * polish; spec 2026-07-16).
 *
 * STACKS ON TOP of the already-shipped TRAINING_BOARD_REDESIGN_ENABLED (which is
 * LIVE — the redesigned DraftBoardRoom). "Flag-off" here means TODAY's
 * DraftBoardRoom + video-based awaiting-open pod, BYTE-IDENTICAL — it does NOT
 * mean the legacy sector board (that is TRAINING_BOARD_REDESIGN_ENABLED's own
 * off-path, a different flag). Do not conflate the two.
 *
 * When true — or with the `?trainingPodV2=1` dev-preview param (the
 * `?trainingBoard=1` idiom) — it delivers L1–L8: a 60s HUMAN pick clock, the
 * board ticker opening AssetResearchModal (row-body still selects), a sticky
 * bottom action bar, and the rebuilt awaiting-open pod (countdown → user
 * draftboard → best-remaining free agents → relocated claims).
 *
 * Client UI + reads only — no new writes, the same applyTrainingPick /
 * place-claim endpoints, the calibration fence untouched. Built/merged DARK; flip
 * in a one-line follow-up PR after a Vercel preview smoke (the
 * TRAINING_BOARD_REDESIGN_ENABLED precedent) — never in the build PR.
 */
export const TRAINING_POD_DRAFT_V2_ENABLED = true;

// The HUMAN pick clock under V2 (L1: 60s; CPU turns unaffected — they resolve
// server-side with no clock). Homed HERE, not in the zero-import, test-locked
// leagueTournament.js, so flag-off stays byte-identical at that module's 20s
// PICK_CLOCK_MS; the single clock consumer (useTrainingDraft) selects between the
// two off isTrainingPodDraftV2On().
export const TRAINING_POD_PICK_CLOCK_MS = 60000;

/**
 * The ONE home for the V2 gate — the flag OR the `?trainingPodV2=1` dev-preview
 * override — so every V2 surface (the lobby clock / ticker-modal / sticky bar and
 * the Phase-2 pod) resolves it identically. SSR/Node-safe (guards `window`); a
 * malformed URL degrades to the flag alone.
 */
export function isTrainingPodDraftV2On() {
  if (TRAINING_POD_DRAFT_V2_ENABLED) return true;
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('trainingPodV2') === '1';
  } catch {
    return false;
  }
}

/**
 * League Training — Training Pod Draft V2, DESKTOP layout for the rebuilt
 * AWAITING-OPEN pod (AwaitingOpenPodView). INDEPENDENT of
 * TRAINING_POD_DRAFT_V2_ENABLED (which is LIVE) — this gates ONLY the desktop
 * reflow of the awaiting-open body, and only at/above the ≥1024 desktop
 * breakpoint (useIsMobile({ tabletBreakpoint: 1023 }), matching the lobby's
 * DraftBoardRoom < 1024 split pixel-for-pixel). Presentation only — no data,
 * scoring, decision, or claims-logic change; the same V2 children are
 * re-arranged, never re-wired; the calibration fence is untouched.
 *
 * Flag-off (default) = today's single-column AwaitingOpenPodView at EVERY width,
 * byte-identical (instant rollback). Mobile (< 1024) is byte-identical flag on
 * or off. Flag-on + desktop = the §2 reflow: full-width countdown + draftboard,
 * a 1.7fr/1fr body (best-remaining free agents left, the claims builder as a
 * sticky rail right), full-width feed, the whole view inside one bounded-height
 * scroll frame the rail pins within (the app's overflow-x:hidden #root breaks a
 * document-scroll sticky — Phase-0 discovery).
 *
 * Built/merged DARK; flip in a one-line follow-up PR after a Vercel preview
 * smoke (the TRAINING_POD_DRAFT_V2_ENABLED precedent) — never in the build PR.
 */
export const TRAINING_POD_DESKTOP_ENABLED = true;

/**
 * The ONE home for the desktop gate — the flag OR the `?trainingPodDesktop=1`
 * dev-preview override (the `?trainingPodV2=1` idiom). SSR/Node-safe (guards
 * `window`); a malformed URL degrades to the flag alone.
 */
export function isTrainingPodDesktopOn() {
  if (TRAINING_POD_DESKTOP_ENABLED) return true;
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('trainingPodDesktop') === '1';
  } catch {
    return false;
  }
}

/**
 * League Training — "Awaiting the Open" REDESIGN of the practice pod's
 * awaiting-open body (AwaitingOpenPodView). Visual + structural, presentation
 * only: layered surfaces, atmosphere, a hero countdown, a full-width draftboard
 * panel, and a wire panel whose rows each carry their own Claim — replacing the
 * standalone two-dropdown claims builder with a per-row swap sheet.
 *
 * INDEPENDENT of TRAINING_POD_DRAFT_V2_ENABLED and TRAINING_POD_DESKTOP_ENABLED
 * (both LIVE) — this gates ONLY the redesigned awaiting-open body. Flag-off
 * (default) = today's screen at every width, byte-identical (instant rollback).
 *
 * No data, scoring, decision, or claims-logic change: the same reads, and claims
 * still go through the unchanged placeClaim({ groupId, dropSymbol, addSymbol })
 * — the redesign changes the UI that CALLS it, never the call. The calibration
 * fence is untouched. Ranked (LiveDraftAwaiting) is a separate component and is
 * not touched.
 *
 * Built/merged DARK; flip in a one-line follow-up PR after a Vercel preview
 * smoke (the TRAINING_POD_DESKTOP_ENABLED precedent) — never in the build PR.
 */
export const AWAITING_OPEN_REDESIGN_ENABLED = true;

/**
 * The ONE home for the redesign gate — the flag OR the `?awaitingOpenRedesign=1`
 * dev-preview override (the `?trainingPodDesktop=1` idiom). SSR/Node-safe
 * (guards `window`); a malformed URL degrades to the flag alone.
 */
export function isAwaitingOpenRedesignOn() {
  if (AWAITING_OPEN_REDESIGN_ENABLED) return true;
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('awaitingOpenRedesign') === '1';
  } catch {
    return false;
  }
}

/**
 * League — PRE-OPEN PHASE ROUTING (the awaiting surface during a battle day's
 * pre-open window).
 *
 * A pod's `status` flips to BATTLE on a DATE-based predicate (anchorDateReached,
 * api/_utils/trainingLifecycle.js), so between the anchor date's midnight and the
 * 9:30 ET open a pod is legitimately BATTLE while the market is still shut. The
 * STATUS IS CORRECT and must not move: BATTLE means "this pod's battle day is
 * today", and two consumers depend on it being set BEFORE the open — the 9:25 ET
 * claims pass (api/_utils/tournamentClaims.js:82-83, whose pre-open ordering
 * api/tournament/place-claim.js:100-107 documents as a precondition) and the
 * orchestrator duty marker (api/_utils/tournamentOrchestrator.js:901-909,
 * :1035-1038) that dispatches ranked fan-out. Only the DISPLAY was wrong: the
 * live body rendered during the pre-open window.
 *
 * When true, the routing sites consume `usePreOpenPhase` (src/hooks/). NO status
 * write moves, no anchor changes, no deploy-timing change, and no claims or duty
 * behavior change: this is display routing only.
 *
 * COVERAGE IS PARTIAL, and deliberately so — read this before flipping:
 *   - practice → AwaitingOpenPodView, plus the training labels (the lobby card,
 *     the climb gate, both draft-completion cards, the waiting room). Requires
 *     TRAINING_POD_DRAFT_V2_ENABLED; under a V2 rollback the practice host reads
 *     live again, and this flag does not change that.
 *   - ranked → the classic column's header copy ONLY, i.e. the pre-deploy window.
 *     Once the agent deploys the arena takes over and reads live until the bell,
 *     as today. Routing ranked to LiveDraftAwaiting was tried and REVERTED: that
 *     surface has no claim controls, and Mon ~06:00-09:24 is the only day-1 claim
 *     window a competitive pod has (place-claim.js:96-97 + the 09:24 wire close).
 *   - the ambient LIVE cascade (THE FIELD, lobby pod cards, funnel, spectate) is
 *     untouched — chokepoint B (leagueAdapter.groupStatusToPodStatus) is deferred
 *     until base-layer pods carry a startAnchor. So with this flag ON a pod can
 *     read "awaiting" on its own screen and "live" in the field list.
 *
 * Flag-off (default) = today's routing at every site, byte-identical — the hook
 * short-circuits to false and never mounts its ticker (no extra re-renders).
 *
 * Built/merged DARK; flip in a one-line follow-up PR after a Vercel preview
 * smoke (the AWAITING_OPEN_REDESIGN_ENABLED precedent) — never in the build PR.
 */
// Pinned by: usePreOpenPhase.test.jsx (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const PREOPEN_PHASE_ROUTING_ENABLED = false;

/**
 * The ONE home for the pre-open routing gate — the flag OR the `?preOpenPhase=1`
 * dev-preview override (the `?awaitingOpenRedesign=1` idiom). SSR/Node-safe
 * (guards `window`); a malformed URL degrades to the flag alone.
 */
export function isPreOpenPhaseRoutingOn() {
  if (PREOPEN_PHASE_ROUTING_ENABLED) return true;
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('preOpenPhase') === '1';
  } catch {
    return false;
  }
}

/**
 * Rule Conflict Reconciler — equip-time DETECTION (shadow-safe half).
 *
 * The agent (BaggerBomb) path has no conflict resolution: contradictory hard
 * constraints (e.g. "Cap Tech 40%" + "≥50% Tech") are both classified
 * "must obey" and the reserved `conflictCheckResult` field is hard-coded null.
 * This flag turns on the canonical reconciler's DETECTION at equip time only:
 * it computes and persists `conflictCheckResult` on the bundle and powers the
 * equip-time warning toast. It does NOT change anything the agent sees at
 * deploy — it is pure shadow/transparency (no prompt-path change).
 *
 * OFF by default. Built/merged DARK; flip after a preview smoke once the
 * reconciler has been observed against real equipped loadouts. The runtime
 * INJECT half (below) is a separate, fence-gated flag and implies this one.
 * See the Rule Conflict Reconciler build spec + RULES_LAUNCHBLOCKER findings.
 */
// Pinned by: ruleConflictReconciler.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const CONFLICT_RECONCILER_DETECT_ENABLED = true;

/**
 * Rule Conflict Reconciler — runtime INJECTION into the cognition prompts.
 *
 * When true, the deploy-time resolve (in the fenced `api/agent/decide.js`
 * call-site) replaces the raw projected `activeRules` with the reconciler's
 * `resolvedRules` before they are frozen into the battle snapshot — so the
 * losing side of a contradiction never reaches the strategy or intraday-eval
 * prompt. This CHANGES what the agent sees; it is the fence-touching half and
 * is gated behind the §7 founder-reviewed commit.
 *
 * INJECT implies DETECT — injection without detection is meaningless. On
 * reconciler error or with this flag off, the path falls back to the raw
 * projected rules (deploy never blocked). OFF by default; flip only after the
 * fenced commit is reviewed for prompt parity and signed off, and after a
 * preview calibration smoke. See the Rule Conflict Reconciler build spec.
 */
// Pinned by: ruleConflictReconciler.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const CONFLICT_RECONCILER_INJECT_ENABLED = true;

/**
 * Release 2 (Fenced Customization Bundle V1.1) — STANDING LEANS activation.
 *
 * Gates the standing-leans surface: the equip/unequip endpoints 404 while
 * false (the scouting-board defense-in-depth pattern), and — once the Phase-2
 * fenced edits land — the shared control renderer refuses to render persisted
 * leans into any prompt while false (read-side guard; data kept, suppression
 * logged per battle+mode-epoch). "No UI" is never the activation control.
 *
 * Boolean by founder ruling D1 (2026-07-10; the spec's STANDING_LEANS_MODE
 * 'off'|'on' resolved to the house *_ENABLED shape — leans have no observe
 * half). OFF at merge (DARK-INERT). Flip is a Release-4 staged-activation-walk
 * step, founder-executed in its own watch window — never in a build PR (the
 * PR #510 lesson).
 *
 * ACTIVATION PREREQUISITE (founder ruling, Phase-2 acceptance 2026-07-10):
 * the leanOverrides chat-side confirmation flow still needs its voice copy
 * (flagged since PR-a). Blocks nothing while dark; must be written before
 * this flag flips.
 */
export const STANDING_LEANS_ENABLED = true;

/**
 * Release 2 (Fenced Customization Bundle V1.1) — TEMPO DIAL activation.
 *
 * Gates the tempo-dial surface: the set-tempo-dial endpoint 404s while false,
 * and the clamp layer (api/_utils/tempoDialClamp.js) resolves EFFECTIVE tempo
 * to 'standard' whenever this is false OR the band table's
 * forKnobConfigVersion mismatches the deployed KNOB_CONFIG_VERSION
 * (version-bound fail-closed, spec changelog #13) — desired state is kept and
 * the divergence is visible via the structured provenance object
 * (suppressionReason), never silent.
 *
 * Boolean by founder ruling D1 (2026-07-10; the spec's TEMPO_DIAL_MODE
 * 'off'|'on' resolved to the house *_ENABLED shape). OFF at merge
 * (DARK-INERT). Flip is a Release-4 staged-activation-walk step,
 * founder-executed in its own watch window — never in a build PR (the PR
 * #510 lesson). The band table itself is PROVISIONAL until promoted from the
 * B4 acceptance report's real-data cross-check.
 */
export const TEMPO_DIAL_ENABLED = true;

/**
 * Archetype Integrity / "Third Path" — tri-state rollout mode.
 *
 * Gates the CHAT-DIRECTIVE half of archetype integrity (the deterministic
 * directive gate, the voice-layer four-zone + third-path injection, and the
 * legacy `directives[]` sanitize) so there is exactly one
 * byte-identical-off regression surface for directives. Three states:
 *
 *   'off'     — byte-identical to today. No archetype-aware gating; the directive
 *               path runs the legacy `normalizeDirective` line verbatim.
 *   'observe' — measurement mode. The model emits the proposal block and the gate
 *               EVALUATES + awaited-logs the outcome, but writes NO directive
 *               (no behavior change persists). Vehicle for the pre-flip reliability
 *               eval; NOT a permanent behavioral tier.
 *   'enforce' — full behavior: the gate mints only core-safe allowlist directives.
 *
 * Default 'off'. Built/merged DARK; advance 'off' → 'observe' → 'enforce' only
 * after the pre-flip reliability eval clears the hard zeros (0 core-reversing
 * directives, 0 "claimed-a-change-but-wrote-null").
 * See docs/audits/20260625_ARCHETYPE_INTEGRITY_BUILD_PLAN_V2.md.
 *
 * Release 2 PR-e (Phase-0 ruling, decoupled 2026-07-10): the Diversifier
 * swap-time sector cap NO LONGER rides this flag — it fires under its own
 * SECTOR_CAP_MODE (below), so the directive walk and the cap walk are
 * independent Release-4 steps. This flag governs chat directives only
 * (master spec V1.2 errata #1).
 *
 * ROLLBACK RULE (founder-adopted 2026-07-10; docs/RELEASE2_ACTIVATION_RUNBOOK.md):
 * while any battle carries an active directive, roll back to 'observe',
 * NEVER 'off'. Know what a rollback does either way: LEAVING 'enforce'
 * permanently retires every in-flight directive (the next tick's epoch
 * record kills it; directives never resurrect — PR-c design), whichever
 * state you land in. There is no "pause" state. The reason 'observe' is
 * still the only correct target: its write path mints NOTHING, while
 * 'off' runs the un-gated legacy normalizeDirective line — directives
 * minted under 'off' would render un-screened on a later re-enforce.
 *
 * Release 2 PR-c (read-side guard): the PROMPT surfaces render a persisted
 * battle.directive ONLY under 'enforce' — under 'off'/'observe' a directive
 * at rest is suppressed (data kept, epoch-logged) so a flip-back is clean on
 * the next eval, and a directive suppressed by a mode flip never resurrects
 * for that battle. The 'off' write path stays the legacy normalizeDirective
 * line verbatim; what changed is that its output no longer REACHES a prompt
 * (master spec §3.6, founder-accepted).
 */
export const ARCHETYPE_INTEGRITY_MODE = 'enforce';

/**
 * THE DIRECTIVE FIT CHECK — the voice files only what it says, and says only
 * what it files (Phase 0: docs/audits/20260915_PHASE0_DIRECTIVE_GATE.md).
 *
 * ONE flag over ONE mechanism in two halves, which is why it is one flag and
 * not two: the prompt asks the model to QUOTE the canonical text of the id it
 * selects, and the gate REQUIRES that quote before it commits. Split them and
 * either half is a defect on its own — a gate that demands a quote the prompt
 * never asked for would null-write every filing, and a prompt that asks for a
 * quote nothing checks is the status quo with extra words.
 *
 * What it turns on (all three together, all read at CALL time):
 *   1. voiceLayerPrompt.js — each menu line carries its annotations
 *      ([cautious register] / [opposite of X]), READ from the data module's
 *      `cautiousRegister` list + ADJUSTMENT_CONFLICT_GROUPS, so the model can
 *      see the dial it is choosing on rather than seven bare strings (Phase 0
 *      Q3). (2026-09-17: the register now comes from the charter's own "More
 *      cautious = ..." sentence instead of a `policy` predicate that disagreed
 *      with it in 4 of 6 archetypes, and the third tag — [concentration:
 *      tighter|wider] — is gone: it rendered the CONSTRAINT VERB, so one string
 *      meant "more concentrated" on SP-04 and "more spread" on DV-01. Build
 *      report 20260916 findings A4 / A5.)
 *   2. voiceLayerPrompt.js — the confirmation rule's acknowledgement asks for
 *      the canonical text word for word instead of "that's my lean now."
 *   3. directiveGate.js — after the membership check, the selected id's
 *      canonical text must appear verbatim (case-sensitive, whitespace
 *      normalized) in the reply, or the turn is the deliberate null
 *      `fit_mismatch` and files nothing (Phase 0 Q5: the reply is written
 *      before the gate runs and is never reconciled with it).
 *
 * NOT gated by this flag: the three forensics fields on the gate record
 * (originalUserAsk / counterOfferText / rejectionReason). They are additive and
 * always on — every turn without them is a turn whose intent can never be
 * recovered (Phase 0 Q7).
 *
 * Shipped FALSE. At flag-off every prompt byte and every gate outcome is
 * byte-identical to the pre-build commit, proved by goldens captured from it
 * (api/_utils/__fixtures__/voiceLayerPrompt.fitCheck.preBuild.golden.json).
 * The flip is its own one-line PR after a founder preview smoke, never a build
 * PR.
 *
 * FLIP ORDER — READ THIS BEFORE FLIPPING. This flag and VOICE_GROUNDING_MODE
 * must NOT be lit together until the grounded confirmation rule carries the
 * quote instruction too.
 *
 * The gate runs on every battle-mode chat turn regardless of `grounded`
 * (chat.js). But the GROUNDED prompt is a different assembly, and the
 * acknowledgement transform above lands only on the shipped one
 * (voiceLayerPrompt.js — the branch reached when `grounded === false`). The
 * grounded confirmation rule says the OPPOSITE of what the gate would then
 * require: "Acknowledge in one sentence. The interface states what was filed.
 * Do not describe what you will do with it." (voiceLayerGrounding.js).
 *
 * So with VOICE_GROUNDING_MODE at 'canary' (for an allowlisted uid) or 'on'
 * AND this flag true, the gate would demand a quote the SENT prompt never
 * asked for, and every typed-path filing would become fit_mismatch. That is
 * exactly the failure the two halves ride one flag to prevent.
 *
 * TODAY THIS CANNOT HAPPEN: VOICE_GROUNDING_MODE is 'shadow' (below), which
 * resolves to 'shadow' for every uid, so `grounded` is false for everyone and
 * the shipped — transformed — prompt is what is sent. Flipping this flag alone,
 * today, is coherent. The hazard is the ORDER, and it belongs to whichever of
 * the two walks moves second.
 *
 * And note what does NOT open it: the VOICE_GROUNDING_CANARY_UIDS env var
 * cannot promote anyone on its own. resolveVoiceGroundingMode consults that
 * list ONLY when the base mode is already 'canary', so while the constant
 * below reads 'shadow' every uid resolves to 'shadow' whatever the env says
 * (proved exhaustively over base × env × uid in the §2 review, 12/12). Reaching
 * the hazard therefore takes a deliberate CODE change to the constant below —
 * a PR someone reviews — not an environment edit. That is the whole mitigation,
 * and it is worth knowing it is a real one.
 *
 * The residual risk is still that this guarantee is a live-value dependency on
 * another flag, held by this comment and one test row rather than by
 * construction. The §2 review's recommendation, carried here so the flipper
 * sees it: prefer shipping one of the two structural fixes BEFORE either walk
 * moves, rather than relying on the ordering contract.
 *
 * The grounded rule was NOT changed here: voiceLayerGrounding.js is outside
 * this build's file list and the grounding walk is out of its scope. The limit
 * is pinned as executable documentation in
 * api/_utils/voiceLayerPrompt.fitCheck.test.js ("the GROUNDED prompt is
 * deliberately NOT transformed"), and raised for founder ruling in
 * docs/audits/20260916_BUILD_DIRECTIVE_FIT_CHECK.md §6 D-3. Resolve it by
 * either giving the grounded rule the same quote instruction in the walk's own
 * PR, or giving the gate a `grounded` input.
 *
 * FLIP MAP — MEASURED, NOT ASSUMED (BUILD_RULES §2: the flip PR reconciles
 * every one of these in the SAME commit, or it reds CI on every other open PR
 * into main). An earlier draft of this map listed only the two pin files and
 * said the goldens "do NOT move". That was wrong: flipping the constant and
 * running the full suite reds **19 rows across 6 files**. Measured at
 * 2026-09-16 on the build branch; re-measure before flipping.
 *
 *   src/config/directiveFitCheckFlags.test.js        1 row  — the dark pin moves to true
 *   src/config/flagPinGuard.test.js                  2 rows — drop this flag from
 *                                                             DARK_BY_DESIGN (its integrity
 *                                                             row reds if a lit flag stays listed)
 *   api/_utils/voiceLayerPrompt.grounding.goldens.test.js  6 rows
 *   api/agent/chat.test.js                           4 rows
 *   api/_utils/directiveGate.test.js                 4 rows
 *   api/_utils/voiceLayerPrompt.test.js              2 rows
 *
 * The last four read the LIVE flag — none of them contains the string
 * DIRECTIVE_FIT_CHECK_ENABLED — so they are behavioural fixtures and prompt
 * goldens, not `expect(FLAG).toBe(...)` pins. flagPinGuard.test.js therefore
 * CANNOT name them for you; only running the suite can. This flip is not a
 * one-line PR.
 *
 * The suites that deliberately do NOT move are the ones that mock this flag
 * explicitly: voiceLayerPrompt.fitCheck.test.js and
 * directiveGate.fitCheck.test.js drive both states per row.
 */
// Pinned by: directiveFitCheckFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const DIRECTIVE_FIT_CHECK_ENABLED = false;

/**
 * Release 2 PR-e — the sector-SLOT rule: tri-state rollout mode.
 *
 * Gates the Diversifier tournament sector-position cap (the ONE mechanical
 * archetype-integrity piece), decoupled from ARCHETYPE_INTEGRITY_MODE by
 * founder ruling (Phase 0, 2026-07-10) so the directive walk and the cap walk
 * are independent Release-4 steps. House tri-state (the
 * ARCHETYPE_INTEGRITY_MODE / RULE_COMPAT_MODE shape, per ruling D1):
 *
 *   'off'     — the flag's own machinery is fully dark: no injection, no
 *               measurement, the guardrails array untouched. (One
 *               flag-INDEPENDENT fix rode PR-e per spec §6: tournament
 *               checkSectorCap divides by the mode's 6-slot book instead of
 *               the momentary held count. Identical on a full book — which
 *               is every known reachable state, since deploys validate
 *               exactly 6 and forced exits defer without a bench — it
 *               differs only on a partial book, where the old math
 *               over-blocked.)
 *   'observe' — measurement mode. Nothing is blocked and the guardrails array
 *               is untouched, but every swap the ENFORCE cap would have
 *               blocked is logged as a `would_block_swap` override (riding
 *               the eval record's guardrailOverrides telemetry) through the
 *               SAME checkSectorCap math and the SAME preconditions as the
 *               enforce path — the measured volume is exactly what enforce
 *               would do, never a drifted parallel rule. Vehicle for reading
 *               real would-block volume before the flip.
 *   'enforce' — full behavior: the min(user, 35%) slot cap is injected at the
 *               cron call site and blocks the 3rd-in-sector swap (2 of 6
 *               allowed, 3 of 6 blocked on the flat6 book).
 *
 * Default 'off' (DARK-INERT at merge). Scope: TOURNAMENT (flat6) Diversifier
 * battles only (founder Option A); user-authored maxSectorWeight guardrails
 * are live Phase-4B behavior and fire under EVERY state of this flag. Walk
 * 'off' → 'observe' → 'enforce' as a Release-4 staged-activation step,
 * founder-executed — never in a build PR (the PR #510 lesson). See
 * api/_utils/agentGuardrails.js (the sector-SLOT rule block).
 *
 * LIVE-STATE PROVENANCE (Sector Cap Activation arc, founder-ruled 2026-07-23):
 * advanced from the malformed 'true' (out-of-vocabulary — it matched neither
 * 'enforce' nor 'observe', so both gates early-returned and the cap behaved as
 * 'off') to 'observe', the first real step of the walk. This activates
 * MEASUREMENT only: every swap the enforce cap would block is recorded as a
 * would_block_swap override in the eval record (durable) plus a [SectorSlot]
 * would_block log line, through the SAME math as enforce — nothing is blocked
 * and no decision changes. Executed as its own deliberate founder-ruled
 * activation step (the PR #600 flip precedent), not bundled with unrelated
 * feature work. 'enforce' remains a LATER, separate founder flip, gated on
 * reading this observe telemetry (runbook Rule 3: the observe read is the
 * go/no-go input).
 */
export const SECTOR_CAP_MODE = 'observe';

/**
 * WS1 — Rule-library archetype scoping: tri-state rollout mode.
 *
 * Gates the whole rule-vs-archetype compatibility feature together (the
 * write-path guard in forgeService, the equip warnings + badges, the observe
 * event stream, and the change-archetype rescan event) so there is exactly one
 * byte-identical-off regression surface. Three states:
 *
 *   'off'     — byte-identical to today. No classification is computed
 *               anywhere; the guard early-returns before touching the map.
 *   'observe' — measurement mode. Every conflict-equip and every
 *               would-be-blocked promotion is classified + logged
 *               (blocked:false), but NOTHING is blocked and no warning UI
 *               renders. Vehicle for reading real conflict-equip volume.
 *   'enforce' — full behavior: soft warning when equipping a core_conflict
 *               rule; hard block on any write that would make a core_conflict
 *               rule must-obey (create-as-hard, promote-to-hard, category
 *               flip, reforge carry-forward — the fence-lite-approved paths).
 *
 * Default 'off'. Built/merged DARK; walk 'off' → 'observe' → 'enforce' only
 * after observing conflict-equip volume (WS1 spec §8.4). INDEPENDENT of
 * ARCHETYPE_INTEGRITY_MODE — the two flags walk separately. The one-time
 * pre-launch cleanup script (WS1 Phase 4) live-runs at or before the enforce
 * step. Classification source: src/data/archetypeRuleCompatibility.js.
 */
export const RULE_COMPAT_MODE = 'enforce';

/**
 * League — Desktop Training Pod tab + Active Training Game card (the desktop
 * League redesign's training addition; see LEAGUE_DESKTOP_TRAINING_POD_BUILD_SPEC).
 *
 ... (rest of the League comment) ...
 * instant one-line rollback.
 */
export const LEAGUE_TRAINING_POD_ENABLED = true;

/**
 * League — Battle Arena "Ask your agent" two-way chat (see the League Agent
 * Chat build spec). When FALSE (default), the arena ask box stays today's
 * local-echo stub — decorative "Ask anything…" placeholder + canned chip
 * echoes, no network — byte-identical to before this build.
 *
 * When TRUE, the ask box becomes a real free-text input + strategy chips that
 * POST to /api/agent/chat (leagueAsk:true) for a grounded in-voice answer,
 * under a per-day question budget (agentChatBudget/{groupId}_{uid}_{dayN}) with
 * a persistent "N left today" counter, an in-voice zero state, and an in-voice
 * failure/retry state.
 *
 * THIS FLAG IS THE COST KILL-SWITCH — every paid ask is behind it. It is
 * independent of the arena flag (LEAGUE_BATTLE_VIEW_V2_ENABLED); flipping it
 * off instantly stops all paid asks and reverts the box to the stub. Built and
 * merged DARK; flip only after a Vercel preview smoke (the TOURNAMENT_TAB_ENABLED
 * precedent).
 */
export const LEAGUE_AGENT_CHAT_ENABLED = true;

/**
 * Command Center — Scouting Board (the READ-section "See what it's eyeing"
 * pre-deploy preview).
 *
 * When FALSE (default), the READ section is byte-identical to today: the primary
 * CTA is "Deploy on this read" (mobile CommandDashboard, desktop ReadColumn) and
 * the deferred "Talk it over · Soon" stub renders unchanged. Nothing calls the
 * read-only GET /api/agent/scouting-board endpoint (which itself 404s while this
 * is false).
 *
 * When TRUE, the primary CTA becomes "See what it's eyeing" → opens the
 * ScoutingBoardSheet (the top-10 archetype-ranked board + the equipped watchlist
 * as a marked group, rendered read-only from the deterministic
 * computeArchetypeRankings). Deploy becomes an action taken FROM the board via
 * the UNCHANGED deployAgent path, with a subtle direct-deploy escape hatch kept
 * in the READ section so deploy is always reachable without opening the board.
 *
 * Built/merged DARK; flip in a one-line follow-up PR after a Vercel preview smoke
 * (the COMMAND_DASHBOARD_DESKTOP_ENABLED precedent) — never in the build PR.
 */
export const SCOUTING_BOARD_ENABLED = true;

/**
 * Correlation Lab — the Correlation Intelligence research surface
 * (rolling group-vs-driver correlations, rolling-40 beta, lead-lag, and
 * correlation-regime inflections with episode-first forward-return base rates).
 *
 * LIVE (TRUE) as of the V1.1 exposure PR. When TRUE, the surface is reachable
 * two ways — the `?correlationDev=1` dev screen (src/components/Research/
 * CorrelationLab.jsx) AND the Discover "Correlations" tab (embedded) — and
 * POST /api/research/correlation serves authenticated requests. This exposure
 * PR ships the flag ON by design (V1.1 spec), after a founder Vercel-preview
 * smoke; it is not the merge-dark build PR.
 *
 * When FALSE (rollback), the app is byte-identical to the pre-V0 state: the
 * Discover tab renders nothing, the `?correlationDev=1` mount effect sets a
 * screen id that never renders, and the endpoint 404s (the scouting-board
 * defense-in-depth pattern) — nothing reachable or callable. Kept as the
 * instant-rollback lever.
 */
export const CORRELATION_LAB_ENABLED = true;

/**
 * Correlation Intelligence V3 Phase 1, Sub-build 1 — the "relationship-quality"
 * bundle (member contribution, SPY-adjusted partial correlation, self-percentile,
 * down/up beta-capture asymmetry, tail co-movement, past stability, driver-side
 * context). Additive-only: pure math over data already fetched.
 *
 * Built/merged DARK (FALSE). While FALSE the two research endpoints compute the
 * new `relationshipQuality` / `rq` blocks NOWHERE and skip the extra SPY fetch in
 * the deep dive, so the payload is byte-identical to today; the Lab renders no new
 * cards. Nested under CORRELATION_LAB_ENABLED — meaningless when the Lab is off.
 *
 * Flip in a one-line follow-up PR after a founder Vercel-preview smoke (the
 * SCOUTING_BOARD_ENABLED precedent) — never in the build PR.
 */
export const CORRELATION_RELATIONSHIP_QUALITY_ENABLED = true;

/**
 * Correlation Intelligence V3 Phase 1, Sub-build 2 — the "synthesis + summary
 * contract" bundle: the server-computed read-quality evidence checklist (a §9
 * checklist of displayed facts, never a score), the honest "since your last
 * scan" comparison + change events, and THE SUMMARY CONTRACT — a versioned,
 * facts-only, presentation-scoped object written beside the payload at cache
 * time (the object Phase-2 voice narration and future agent consumption both
 * read — "one engine, two surfaces").
 *
 * Built/merged DARK (FALSE). Dependency rule enforced at every read site:
 * synthesis features compute/serialize/render ONLY when
 * CORRELATION_SYNTHESIS_ENABLED && CORRELATION_RELATIONSHIP_QUALITY_ENABLED
 * (see synthesisActive() in api/research/summaryContract.js — the on/off
 * misconfiguration short-circuits dark with a single console.warn). While
 * FALSE the two research endpoints add NO summaryContract / evidence /
 * comparison / dataAsOf fields, so the payload is byte-identical to today and
 * the Lab renders no new panels. Nested under CORRELATION_LAB_ENABLED and
 * sibling to CORRELATION_RELATIONSHIP_QUALITY_ENABLED — meaningless when either
 * is off.
 *
 * Flip in a one-line follow-up PR after a founder Vercel-preview smoke (the
 * CORRELATION_RELATIONSHIP_QUALITY_ENABLED precedent) — never in the build PR.
 */
export const CORRELATION_SYNTHESIS_ENABLED = true;

/**
 * Correlation Intelligence V3 Phase 1, Sub-build 3 — the EXTENDED DRIVER TIER
 * (Bucket A): five two-gate-verified drivers (SMH, CPER, FXY, TIP, EMLC) added
 * to the registry as `tier: 'extended'`, admitted as an OPT-IN tier under the
 * comparison-tax rule (more drivers dim existing signals, so the default scan
 * stays at the 25 core drivers).
 *
 * Built/merged DARK (FALSE). While FALSE the extended drivers enter NO scan and
 * NO deep dive: the scan's effective driver universe is core-only (so its docId
 * salt + driverUniverseHash are byte-identical to the pre-build 25-driver
 * values — the dark merge orphans no cached scans and manufactures zero
 * not_comparable days), the deep-dive endpoint treats an extended driver key as
 * invalid_driver, and the Lab hides the "Include extended drivers (5)" scan
 * toggle + the "Extended" driver-select optgroup. When TRUE, the extended
 * drivers are scannable ONLY when the user ALSO opts in via the toggle
 * (includeExtended in the scan body); the deep-dive optgroup becomes selectable.
 *
 * The permanent driver-audit tool (api/research/driver-audit.js, reachable via
 * the Lab's `?driverAudit=1` dev param) is gated by CORRELATION_LAB_ENABLED
 * ONLY — NOT this flag — so the two-gate verification runs on the Vercel preview
 * while this tier is still dark. Nested under CORRELATION_LAB_ENABLED —
 * meaningless when the Lab is off.
 *
 * Flip in a one-line follow-up PR after the founder two-gate smoke (the
 * CORRELATION_SYNTHESIS_ENABLED precedent) — never in the build PR.
 */
export const CORRELATION_EXTENDED_DRIVERS_ENABLED = true;

/**
 * Correlation Intelligence V3 Phase 2 — THE NARRATED LAB: the deep-dive
 * "Explain this read" voice. A pure deterministic plan builder selects/orders
 * the claims and renders every value/date/name/band into exact display spans;
 * the Gemma voice layer RENDERS (picks one approved sentence frame per claim +
 * a connective — it does not author); a conformance validator proves the output
 * is exactly the approved frames with the exact server spans and nothing else.
 * On any plan/model/validation failure the endpoint falls to the deterministic
 * verdict sentence (the "standard summary"). Narration is strictly downstream of
 * an existing cached deep-dive contract — the endpoint never recomputes.
 *
 * Built/merged DARK (FALSE). Dependency rule enforced at the endpoint guard via
 * narrationActive() (mirrors summaryContract.js:synthesisActive): narration
 * computes/serves ONLY when CORRELATION_NARRATION_ENABLED &&
 * CORRELATION_LAB_ENABLED && CORRELATION_SYNTHESIS_ENABLED (the summary contract
 * only exists when synthesis is active, so narration must require it). While
 * FALSE the /api/research/correlation-narrate endpoint returns 404 (dark =
 * invisible: no reads, no model call, no cache writes) and the Lab renders no
 * "Explain this read" button — byte-identical to today. Nested under
 * CORRELATION_LAB_ENABLED and sibling to CORRELATION_SYNTHESIS_ENABLED —
 * meaningless when either is off.
 *
 * Flip in a one-line follow-up PR after a founder Vercel-preview smoke (the
 * CORRELATION_SYNTHESIS_ENABLED precedent) — never in the build PR.
 */
export const CORRELATION_NARRATION_ENABLED = true;

/**
 * Agent Learning System — L1 Foundation (Phase A) raw capture.
 *
 * THE MASTER KILL-SWITCH for the entire L1 capture path. Release classification
 * DARK-INERT: nothing user-visible, nothing reaching the trading brain, no
 * scoring change. Architecture V1.3 (FROZEN).
 *
 * When FALSE, the capture path is a strict NO-OP: the swap-execution sites in
 * api/cron/agent-evaluate.js never build a receipt, never touch Firestore, and
 * add ZERO latency to the decision path (every call is behind a plain
 * `if (LEARNING_L1_CAPTURE_ENABLED ...)` guard, so the branch is never
 * entered). The decision path is byte-identical to pre-L1 behavior.
 *
 * When TRUE, each executed swap at a flag-enabled capture site emits a RAW
 * receipt to learningReceipts/{battleId}/receipts/{receiptId} via the Admin
 * SDK — raw fields only, no derived metric, no classification, no scoring. All
 * four learning collections are server-write-only (firestore.rules), so the
 * receipt can only be written server-side.
 *
 * LIVE-STATE PROVENANCE (Corpus Capture Patch Phase 0b, corrects a stale
 * comment): flipped false → true in the deliberate founder flip PR #600
 * (commit 400423d8, merged 873d4791, 2026-07-12 — flags-only diff, the
 * CORRELATION_SYNTHESIS precedent). Capture at the original autopilot site is
 * LIVE. Founder ruling (Phase 1 greenlight memo, July 21 2026): the flag stays
 * TRUE — a reset inside a build PR would itself be a flag flip in a build PR,
 * and would open a corpus gap in the only class currently captured. The three
 * W2 expansion classes are additionally gated by
 * LEARNING_L1_CAPTURE_EXPANSION_ENABLED below (false until its own flip PR).
 *
 * This flag gates capture ONLY. No dossier/atom writer ships in L1 (Signal
 * Capture Rider §5: capture only, pre-launch).
 */
export const LEARNING_L1_CAPTURE_ENABLED = true;

/**
 * Corpus Capture Patch W2 — L1 capture EXPANSION to the three swap classes
 * beyond the original autopilot site: risk-manager executed swaps (incl.
 * stagnation forced rotation), gameplan-meeting rotations, and co-pilot
 * proposal execution (approved AND expired-auto-exec — 4 physical sites).
 *
 * AND-gated with the master kill-switch at every new site:
 *   LEARNING_L1_CAPTURE_ENABLED && LEARNING_L1_CAPTURE_EXPANSION_ENABLED
 *     && classifyEvidence(...) === 'live_agent'
 * The master switch still kills everything; this flag only widens CLASS
 * coverage, and has no effect on the original autopilot site.
 *
 * FALSE at merge (founder ruling, Phase 1 greenlight memo July 21 2026):
 * merge dark → preview smoke → deliberate flip PR, flipped together with
 * REGIME_STAMP_ENABLED. The flip date is the corpus E2 full-coverage boundary
 * recorded in that flip PR.
 */
// Pinned by: agent-evaluate.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const LEARNING_L1_CAPTURE_EXPANSION_ENABLED = true;

/**
 * Corpus Capture Patch W3 — write-once `regimeAtStart` stamp on agentBattles
 * docs, applied at each battle's FIRST evaluation tick (never overwritten).
 *
 * Stamps the market-level regime (bull|correction|bear|recovery) from the
 * indexIntelligence/marketContext doc the evaluator ALREADY loads per battle —
 * zero added Firestore reads. Unblocks T3 (regime-conditional) learning and
 * trial conditioning, forward-only from the flip date (Discovery A3 / P1
 * flag #1: the regime source docs are overwrite-in-place singletons, so
 * "regime during battle X" is unrecoverable retroactively).
 *
 * FALSE at merge (Build Spec §5.6): merge dark → preview smoke → deliberate
 * flip PR, flipped together with LEARNING_L1_CAPTURE_EXPANSION_ENABLED above.
 * When FALSE: no battle doc is touched, no stamp field exists — prior
 * behavior exactly. Already-stamped docs are inert data either way.
 */
// Pinned by: agent-evaluate.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const REGIME_STAMP_ENABLED = true;

/**
 * BaggerBomb opener — lazy regeneration + template floor.
 *
 * The deploy-time first-message ("opener") is generated synchronously inside the
 * deploy invocation under a ~15s Gemma abort (api/agent/decide.js
 * generateFirstMessageOnDeploy). When Gemma is slow it aborts mid-stream, the
 * truncated body fails JSON parse, and the try/catch swallows it (non-blocking by
 * design) — so tiered battles intermittently open with a silent timeline.
 *
 * LIVE — TRUE since the flip PR that followed the dark build's preview smoke.
 * When FALSE, behavior is byte-identical to the pre-build state: the client makes
 * no ensure-opener call and POST /api/agent/ensure-opener no-ops. When TRUE, the
 * Command Center chat (AgentChat) checks on mount whether a first_message exists
 * and, if not, calls the non-fenced POST /api/agent/ensure-opener, which
 * regenerates the opener with a patient (~40s) budget + one deadline-bounded retry
 * and falls back to a deterministic template opener — so a fresh-deploy chat is
 * never silent. Late-open (chat already has content, no opener) is a deliberate
 * no-op. The fenced deploy path is untouched.
 *
 * Built/merged DARK and flipped in a one-line follow-up PR after a Vercel preview
 * smoke (the SCOUTING_BOARD_ENABLED precedent) — never in the build PR. That flip
 * left this docstring and ensure-opener.js's header calling FALSE the default,
 * which is the stale-header class BUILD_RULES §2 and §6 both name; both now say
 * what the value is.
 */
// Pinned by: src/components/Agent/AgentChat.ensureOpener.jsdom.test.jsx (the live-state pin — update the assertion in the flip commit if this is ever walked back).
export const OPENER_LAZY_FALLBACK_ENABLED = true;

/**
 * League Tournament — advancement freeze, UNFROZEN 2026-08-07 (was an emergency
 * tourniquet; lifted after adjudication). The functional change here is the single
 * flag value; the rest is truthful history for future readers.
 *
 * HISTORY. The scoring-model anomaly (docs/audits/LEAGUE_SCORING_ANOMALY_* ) let
 * poisoned composites accumulate on in-flight groups, so this flag froze the
 * irreversible consumers of those composites BEFORE the day-5 ingestion: bracket
 * advancement + finalScores, the career-rank ratchet (appliedGroups), and the
 * seasonal-leaderboard week-row upsert for non-training battle groups. Banking was
 * never touched — daily scores kept recording. Guarded call sites: runFridayAdvancement
 * (primary), runWeekSideEffects (defense-in-depth belt), and upsertLeaderboardForGroups
 * (both leaderboard call sites at one knowledge point). When frozen, each logged
 * loudly and returned without writes.
 *
 * UNFREEZE (this change, founder-gated). Adjudicated in
 * docs/audits/20260807_LC_UNFREEZE_ADJUDICATION_CLOSEOUT_V1.md: no scoring-model
 * defect was identified (the §7 pass reduces to "confirm no model change needed"),
 * the poisoned cohort was voided (L-A) and recurrence bounded (L-B Guards 1/2), and
 * the status=='battle' enumeration was empty — re-verified immediately before merge
 * (scripts/lc-fork-adjudication.js Part A), the one time-sensitive precondition. On a
 * zero-BATTLE board the flip is a write no-op (tournamentAdvancement.test.js:281);
 * its first real effect is the next cohort's day-5 Friday close.
 */
export const TOURNAMENT_ADVANCEMENT_FROZEN = false;

/**
 * Weekly Ladder — placement points on the MONTHLY board (20260831_WEEKLY_LADDER_
 * BUILD_SPEC_V1; structure ruling 20260831_TOURNAMENT_STRUCTURE_RULING_V1).
 *
 * Re-scores tournamentLeaderboards/{YYYY-MM} from cumulative COMPOSITE to
 * cumulative PLACEMENT POINTS — 1st=3, 2nd=2, 3rd=1, 4th=0 off the existing
 * day-5 finish order (lockTopTwo/rankByScores, the L-B Guard 2 clamped
 * composite). The composite is retained as the stored TIEBREAK input (margin
 * over the group average), never the primary key.
 *
 * MONTHLY BOARD, NOT THE CAREER DOC (the spec's load-bearing decision). The
 * career tournamentRanks path applies cpuFarmGuard — which DISCOUNTS a human's
 * gain in CPU-heavy groups, the exact penalty the ruling overturns — and never
 * resets. The monthly board has no code path to that guard (cpuFarmGuard's only
 * production caller is computeRankBreakdown, whose only caller is
 * tournamentRank.js) and resets by design ("reset = a new doc key"). Career RP
 * is UNTOUCHED by this flag in either position.
 *
 * When FALSE (default), flag-off is BYTE-IDENTICAL: buildGroupWeekRows emits
 * today's exact row shape (no new keys), the month entry carries no new fields,
 * and the reader sorts on cumulative composite exactly as it does today.
 *
 * AWARDED ON FINAL ROWS ONLY (founder decision D1, 2026-08-31): placement is a
 * week's OUTCOME, so a row scores only once `final` is true (day 5 banked or the
 * group COMPLETE). The nightly aggregation rewrites in-progress BATTLE rows
 * every night; awarding provisionally would churn the season rank on days
 * nobody finished. 0 until Friday is honest — THE FIELD carries live state.
 *
 * CPU seats are FULL entries (ruling §4): archetype-named via cpuAgentName,
 * visually marked, and eligible for ANY position INCLUDING first. A CPU topping
 * the board is ACCEPTED and pre-ruled — a finding worth investigating (it would
 * say something real about whether agent decision-making beats holding a drafted
 * book), never a defect to patch. Build no eligibility exclusion. CPUs come off
 * the board later as a DISPLAY change when the human population makes them
 * unnecessary — not a data migration; their presence is not load-bearing.
 *
 * ⚠ FLIP ON A MONTH BOUNDARY (build review finding F2, CONFIRMED with a repro).
 * This board is month-keyed and "reset = a new doc key", so a flip taken at a
 * month boundary scores the new month whole, from week 1. A MID-MONTH flip does
 * NOT backfill: weeks finalized while the flag was dark carry no placement keys,
 * and the nightly aggregation only revisits BATTLE groups (a closed week is
 * COMPLETE and never revisited), so those weeks count 0 toward the month Σ
 * PERMANENTLY — a 3-week month would score out of its 1 post-flip week, and the
 * §9 decomposition would show "—" against weeks a player actually won. The
 * ordering failure this also caused (the whole board collapsing to alphabetical
 * by odUserId) is fixed — rankLeaderboardEntries now falls back to cumulative
 * composite before the id — but that only makes a mid-month flip GRACEFUL, not
 * correct. If a mid-month flip is ever required, backfill first: every seat's
 * composite for a given week is recoverable from the month doc alone
 * (entries[*].weeks[groupId].points across the four seats), so the placement and
 * margin for a dark week can be re-derived without re-reading group docs.
 *
 * Built/merged DARK behind this flag; flip in a one-line follow-up PR after a
 * Vercel preview smoke (the AGENT_PRESENCE_ENABLED precedent) — never in the
 * build PR.
 */
// Pinned by: src/utils/weeklyLadderSurface.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2; the flag-ON battery api/_utils/tournamentLeaderboard.placement.test.js MOCKS this flag rather than pinning it).
export const WEEKLY_LADDER_PLACEMENT_ENABLED = false;

/**
 * Agent Presence — the reactive agent FACE (expression rig + mood baseline + event
 * reactions + environment layer). A READ-ONLY display surface: it writes nothing,
 * gates nothing, and is removable without touching any scoring or decision path.
 *
 * When FALSE (default), NOTHING renders it — every mount site is gated on
 * isAgentPresenceOn(), so flag-off is byte-identical to today (the orb/Bot glyph render
 * exactly as before, and the binding hook never runs because the mount wrapper is never
 * mounted). When TRUE — or via the `?agentPresence=1` dev-preview param (the
 * `?trainingPodV2=1` idiom) — the face mounts on the League arena, the 1v1/training
 * battle screen, and the Command Center, each bound to the EXACT standing that surface
 * already renders (league youRank, 1v1 displayPlayerScore vs opponent, Command identity)
 * — never a parallel recompute (§9 display-agreement).
 *
 * Built/merged DARK behind this flag; flip in a one-line follow-up PR after a Vercel
 * preview smoke (the SCOUTING_BOARD_ENABLED precedent) — never in the build PR.
 */
export const AGENT_PRESENCE_ENABLED = true;

/**
 * The ONE home for the Agent Presence gate — the flag OR the `?agentPresence=1`
 * dev-preview override. SSR/Node-safe (guards `window`); a malformed URL degrades to
 * the flag alone.
 */
export function isAgentPresenceOn() {
  if (AGENT_PRESENCE_ENABLED) return true;
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('agentPresence') === '1';
  } catch {
    return false;
  }
}

/**
 * Matchups Backdrop — ports the PvP view's animated particle/constellation
 * canvas (BaggerBombBackground) plus the bolder gradient tier headers into the
 * AgentBattleScreen Matchups tab, recolored to the teal/mint accent. Merged
 * DARK; flip in a one-line follow-up after a Vercel preview smoke (the
 * AGENT_PRESENCE_ENABLED precedent). One shared flag gates BOTH the backdrop
 * and the header/band restyle so flag-off is byte-identical to today.
 */
export const MATCHUPS_BACKDROP_ENABLED = true;

/**
 * The ONE home for the Matchups Backdrop gate — the flag OR the
 * `?matchupsBackdrop=1` dev-preview override. SSR/Node-safe (guards `window`);
 * a malformed URL degrades to the flag alone (the isAgentPresenceOn idiom).
 */
export function isMatchupsBackdropOn() {
  if (MATCHUPS_BACKDROP_ENABLED) return true;
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('matchupsBackdrop') === '1';
  } catch {
    return false;
  }
}

/**
 * Training-Pod P0 R3 — the rolling stale-pod expiry backstop. Server-only; gates
 * the orchestrator's per-tick `expireStaleTrainingPods` sweep, which retires
 * training pods stranded pre-BATTLE (FORMING orphans, wedged DRAFTING drafts the
 * idle sweep could not complete, AWAITING_OPEN pods whose flip failed past an
 * arrived anchor) to the terminal EXPIRED status — never retro-advancing them
 * (D1 ruling). Founder-gated one-time cleanup runs the SAME core off-tick.
 *
 * When FALSE (DEFAULT, merge-dark), the orchestrator NEVER calls the sweep — the
 * tick is byte-identical to today, no pod is ever expired, and the EXPIRED
 * machinery (R2) stays inert. When TRUE, the sweep runs each weekday-morning tick
 * AFTER the awaiting-open flip (so a pod that legitimately advances this tick is
 * never expired; the expire's state+version precondition closes the residual
 * race). Flip only after a founder smoke — never in a build PR (the
 * LEAGUE_NEXT_ARC_ENABLED / PR #510 precedent).
 */
export const POD_EXPIRY_SWEEP_ENABLED = false;

/**
 * Archetype Architecture Phase 2 (P2.4a) — the equip-time build compiler
 * (PHASE2_BUILD_BRIEF_V1; Spec §4.4 + A-2/A-3). Gates the settings
 * endpoints' in-transaction compile + CompiledBuild write
 * (api/_utils/compileOnSettingsChange.js) to
 * agents/{agentId}/compiledBuilds/{gameMode}.
 *
 * When FALSE (DEFAULT, merge-dark), every settings endpoint is byte-identical
 * to today: the compile helper returns null before any read or write — zero
 * added Firestore I/O, no response-shape change. When TRUE (preview smoke
 * only in Phase 2), each real settings write also compiles the build for the
 * live deploy modes inside the SAME transaction (riding the structural
 * settingsRev increment — A-3: the compile mints the revision) and the
 * response gains a compilePreviews payload.
 *
 * PRODUCTION ACTIVATION IS DOUBLE-GATED: this flag AND the §5.6/A-4 metadata
 * completeness gate (api/_utils/activationGate.js), which FAILS by design
 * until Phases 3-4 author the corpus. Until then enabled compiles record
 * validation.pass=false (truthful pre-authoring state) and the deploy path
 * (P2.4b) refuses them. Flip only via a deliberate founder flag-flip PR with
 * a green activation gate — NEVER in a build PR (Phase 2 exit criteria).
 */
// Pinned by: compileOnSettingsChange.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const COMPILER_ENABLED = false;

/**
 * Archetype Architecture Phase 2 (P2.5, §7-signed fence contact) — the
 * ResolvedAgentManifest block written by createAgentBattle adjacent to the
 * agentContext snapshot (Spec DR-6; built by the non-fenced
 * api/_utils/resolvedAgentManifest.js kernel, the buildCustomizationSnapshot
 * precedent).
 *
 * When FALSE (DEFAULT, merge-dark), createAgentBattle's battle doc is
 * byte-identical to today (the P4 equivalence battery is the lock) — no
 * manifest field exists anywhere. When TRUE (preview smoke only in Phase 2),
 * every new battle doc carries `resolvedAgentManifest` frozen at creation:
 * create-only-after-start holds by construction (the block is born in the
 * single creation write and no updater exists — R1-4).
 *
 * ZERO READERS MIGRATE IN PHASE 2: agentContext remains the runtime
 * authority throughout (brief P2.5); manifest-read migration is a later
 * phase behind the DR-10 two-stage validation. Flip only via a deliberate
 * founder flag-flip PR — never in a build PR.
 */
// Pinned by: agentBattleService.test.js, resolvedAgentManifest.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const MANIFEST_WRITE_ENABLED = true;

/**
 * Archetype Architecture Phase 2 (P2.6) — tick-side shadow assembly +
 * behavior-record envelope plumbing (api/_utils/shadowAssemblyCapture.js,
 * riding the non-fenced agent-evaluate tick; Spec DR-10 stage 1 + A-1 +
 * §6.3/§6.4).
 *
 * DARK since 2026-09-12 (this flip). It shipped TRUE from 2026-07-24 (PR
 * #671) until then, accumulating the DR-10 stage-1 corpus: per battle-tick,
 * the three eval-prompt parts built twice — once from the live agentContext,
 * once with the manifest's frozen layers overlaid — diffed, and written
 * awaited to agentBattles/{id}/shadowDiffs/{tickId}, with the §6.3 gate
 * aggregates riding finalUpdate and the §6.4 battleSettlements record at
 * completion.
 *
 * WHY IT IS OFF: the corpus has exactly one consumer — the offline
 * scripts/paired-eval-harness.js, run once (2026-07-31) for the DR-13
 * identity-block flip, which shipped. The manifest-read migration the corpus
 * was built to gate has no build and was re-routed by the composition arc
 * (the advisory read edge went through the fenced assembler, not the shadow's
 * projection); api/ and src/ have zero readers; the gate aggregates and
 * settlement records were never read by anything. The capture cost three
 * deadline-less awaits — two buildLiveContextBlock calls and the shadowDiffs
 * .create() — sequenced before battleRef.update(finalUpdate), where a hang
 * takes this battle's write and every later battle in the serial loop.
 *
 * When FALSE (today), the tick never calls into the capture module — no
 * shadow prompts, no shadowDiffs writes, no gate-aggregate growth, no
 * settlement records, no receiptCoverage stamp. Nothing the decider sees
 * changes: its own prompt, call, decision, swaps, stamps and record are
 * untouched.
 *
 * The historical corpus is PRESERVED and stays queryable by the harness (530
 * shadowDiffs / 78 battleSettlements at the 2026-08-20 census). A re-flip
 * needs the DARK_BY_DESIGN ceremony (src/config/flagPinGuard.test.js) and is
 * its own deliberate founder flag-flip PR — never a build PR.
 */
// Pinned by: shadowAssemblyCapture.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const SHADOW_ASSEMBLY_ENABLED = false;

/**
 * Deploy Ceremony Phase 2 (client) — the three-act deploy experience:
 * hold-to-deploy (Act 1), the checkpoint-driven thinking theater (Act 2), and
 * the "ready for battle" reveal (Act 3). Consumes the inert deployProgress
 * telemetry shipped in Phase 1 (api/agent/decide.js).
 *
 * When FALSE (DEFAULT, merge-dark), the deploy flow is byte-identical to today:
 * tap-to-deploy, the "Deploying…" label, the fused auto-navigation to the battle
 * view, and the existing success toast — instant rollback. When TRUE — or via
 * the `?deployCeremony=1` dev-preview override (the isAgentPresenceOn idiom) —
 * every deploy entry point becomes hold-to-arm, the ceremony overlay mounts at
 * the Command Dashboard shell and plays the real pipeline, and navigation defers
 * to an explicit "Enter the battle" CTA (the toast is suppressed — the reveal is
 * the confirmation).
 *
 * Built/merged DARK behind this flag; flip in a one-line follow-up PR after a
 * Vercel preview smoke (the AGENT_PRESENCE_ENABLED / SCOUTING_BOARD_ENABLED
 * precedent) — never in the build PR.
 */
export const DEPLOY_CEREMONY_ENABLED = true;

/**
 * The ONE home for the Deploy Ceremony gate — the flag OR the `?deployCeremony=1`
 * dev-preview override. SSR/Node-safe (guards `window`); a malformed URL degrades
 * to the flag alone. Every ceremony mount/branch site gates on this helper, never
 * on the raw const and never on a locally re-read URL (the isAgentPresenceOn /
 * isTrainingPodDraftV2On precedent).
 */
export function isDeployCeremonyOn() {
  if (DEPLOY_CEREMONY_ENABLED) return true;
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('deployCeremony') === '1';
  } catch {
    return false;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// FANTASYTIMES WIRE — AGENT-FIRST NEWS ARC, Phase 1 (Spec V1.5 §4.8)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Wire timing instrumentation ONLY — per-seam duration samples into the
 * server-only wireMetrics/{date} doc (bounded, cap 500/seam/day). Never
 * touches the model request object or persisted story content; §9 asserts
 * payload equality with metrics on, writes off. Flipped FIRST (≥3 trading
 * days) to capture the p95 baseline the §6.1 gate needs.
 */
// Pinned by: wireFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const WIRE_METRICS_ENABLED = true;

/**
 * The Wire writes machinery: cloned extended tool schema + agentFacts prompt
 * instructions + extraction + validation + digest rendering + the story/
 * envelope batch, Wire transaction, receipts, chainId resolution, replay
 * sweep + raised max_tokens.
 *
 * FLIPPED false→true in the founder flag-flip PR (runway: metrics → 3-day
 * baseline → writes), after the metrics baseline landed. The merge-dark era is
 * over; TRUE is now the default and FALSE is the deliberate-revert path.
 *
 * When TRUE (CURRENT): the write path is live — the cloned schema, agentFacts
 * extraction/validation, digest rendering, and the persisted story/envelope
 * batch all run. When FALSE (revert): byte-identical outbound model request
 * payload vs the pre-Wire build (M8 — dark means dark at the model-request
 * boundary). Continuity/newsline still AND-gate on this flag via getWireFlags,
 * so each stays dark until its own flip. Each flip is its own one-line PR
 * (Pushed ≠ deployed).
 */
// Pinned by: wireFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const WIRE_WRITES_ENABLED = true;

/**
 * Continuity prompt block ONLY (reporter's recent Wire digests + eventTypes
 * + dates — never headlines; P7/M3). Requires WIRE_WRITES_ENABLED — the
 * resolution point (api/_utils/wireFlags.js getWireFlags) enforces the
 * dependency; this raw const is never read directly by call sites.
 */
// Pinned by: wireFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const CONTINUITY_MEMORY_ENABLED = false;

/**
 * Phase 2 N1 — Gemma's voiceLayerCache newsLine (Spec V1.2 N1, V1.5 R4-M2).
 * The first NEW Wire consumer: per portfolio/bench symbol, whole validated
 * digests packed under the exact 240-code-unit ceiling into
 * voiceLayerCache/{battleId}.newsLines, rendered by voiceLayerPrompt's
 * battle fall-through as referenceable context (never instructions).
 *
 * Requires WIRE_WRITES_ENABLED — with writes off there are no Wire entries
 * to read; the resolution point (getWireFlags) enforces the dependency, so
 * no call site can run the newsLine dark-solo. FALSE = the cache doc is
 * field-wise byte-identical (no newsLines field) and the cache tick makes
 * ZERO fantasyTimesWire reads (P2-1). Flips LAST in the §4 sequence (step
 * 7), at founder discretion once dark-solo health is clean. Each flip is
 * its own one-line PR (Pushed ≠ deployed).
 */
// Pinned by: wireFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const WIRE_NEWSLINE_ENABLED = false;

/**
 * Phase 2 N3 — the weekly Wire editorial review (Spec V1.2 N3, D-P2-12).
 * Rides process-pending-reflections.js as the LAST tenant (reflections →
 * Wire sweep → editorial, R4-M5): Sunday-gated, isolated try/catch, hard
 * remaining-budget floor — the sweep's budget is inviolable. One
 * deterministic sample per ISO week; deterministic adapters carry the
 * gate-bearing verdicts; one Sonnet advisory pass (chunked) rides
 * wireModelCall; immutable runs land in wireEditorial/{isoWeek} with a
 * 90-day retention (memos must outlive the Wire's 30-day window).
 *
 * No hard flag dependency (per the V1.2 flag table): with writes off the
 * weekly frame is empty and runs record `insufficient` — harmless but
 * noisy, which is why the §4 sequence flips this AFTER WIRE_WRITES_ENABLED
 * (step 5: first Sunday after writes). Each flip is its own one-line PR
 * (Pushed ≠ deployed).
 */
// Pinned by: wireFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const EDITORIAL_REVIEW_ENABLED = false;

/**
 * Alex Catalyst Confirmation mini-arc (spec V1.1) — F2 EXA supplementary
 * retrieval. When TRUE (CURRENT), the confirmed-mover path fetches EXA /search
 * evidence alongside the existing Sonar/validated-catalyst context and presents
 * both as tagged [ATTRIBUTION]/[CONTEXT] channels. When FALSE (revert): the EXA
 * client is never called and the mover prompt is byte-identical to the
 * Sonar-only path — F1+F3 carry ZERO EXA dependency (C9). DOWNGRADED per the C9
 * ruling: EXA is supplementary context, never a catalyst oracle; the honesty
 * floor is the expected outcome on fast movers.
 *
 * FLIPPED false→true in the founder flag-flip PR after the cost/quality review.
 * TRUE is now the default and FALSE is the deliberate-revert path. Not pinned by
 * any test and not dark-by-design, so the flag-pin guard needs no entry for it;
 * its sole consumer is api/fantasytimes/generate-mover.js. Its own one-line PR
 * (Pushed ≠ deployed).
 */
export const EXA_RETRIEVAL_ENABLED = true;

/**
 * Archetype Architecture Phase 3 — the DR-13 eval-time archetype identity
 * block (api/_utils/evalIdentityBlocks.js): the six constitution golden
 * renders + the shared subordination clause, injected into the Haiku
 * eval/swap system prompt ahead of the first ━━━ banner (both prompt
 * variants) once the Commit-2 fenced splice lands.
 *
 * FLIPPED false→true in the founder flag-flip PR (DR-13 endgame) — after the
 * dark-flag shadow diff and the offline paired-eval harness pass: 840 paired
 * decisions across temperature 0 and 0.4 showed zero decision drift, so the
 * block ships identity-consistent reasoning (conviction + rationale framing),
 * not measurable decision change at current gate settings. The merge-dark era
 * is over; TRUE is now the default and FALSE is the deliberate-revert path.
 *
 * When TRUE (CURRENT), each eval tick's system prompt carries the deciding
 * archetype's identity block — the P4 battery's real-flag eval snapshots are
 * now the ON-state texts of record; unknown keys omit the block and log (never
 * substitute a default identity). When FALSE (revert), renderEvalIdentityBlock
 * returns '' for every key and both eval prompts fall back byte-identical to
 * the pre-DR-13 text — the inert path the injection test's flag-off arm locks.
 */
export const EVAL_IDENTITY_BLOCK_ENABLED = true;

/**
 * Swap Motive Observability (Tier 1) — the Film Room per-swap reason display.
 * DARK by default: when FALSE (CURRENT), the ledger renders exactly as before
 * (no reason label on any row) — flag-off byte-identity. When TRUE, every swap
 * row shows a human-readable reason (declared model motive, deterministic
 * taxonomy, or the graceful legacy fallback). Gates ONLY the display; the
 * swap_type schema field and the swapMotive stamp are additive and inert to all
 * existing readers regardless of this flag.
 */
export const SWAP_MOTIVE_DISPLAY_ENABLED = false;

/**
 * Fundamental Wire arc (Jul 25 2026 founder rulings D1–D7) — the peerRankings→
 * stockRankings fundamentals mirror (Commit 1) + the two prompt render blocks
 * that read it (Commit 2, §7-gated fence contact).
 *
 * FLIPPED false→true Jul 25 2026 (`c45f936c`, founder flag-flip commit after
 * the production-shaped render smoke). The merge-dark era is over; the
 * paragraphs below are the ON contract, and the off-state is now the
 * deliberate-revert path, not the default.
 *
 * When TRUE (CURRENT): each stock entry carries the D3 minimal fundamentals
 * sub-object (trailingPE {value, sectorMedian}, priceBookMRQ,
 * revenueGrowthPct, marketCapClass, earningsRevisions30d, real-only beatRate,
 * surpriseMagPercentile, per-entry peerRankings computedAt provenance), the
 * eval live-context gains the FUNDAMENTALS block (holdings + bench), and the
 * draft/board CSV gains the 3-column fundamentals group — all null-honest
 * (an absent metric renders absent, never a neutral default). The P4 battery
 * file snapshots are the ON-state texts of record (the two
 * buildStrategySystemPrompt goldens + buildPortfolioSystemPrompt carry the
 * PE_VS_SECT|REVG_PCT|MCAP_CLS vocabulary); the on-state FUNDAMENTALS block
 * and market-CSV goldens live under `api/_utils/__fundwire_snapshots__/`.
 *
 * When FALSE (revert path): compute-index-intelligence writes a stockRankings
 * doc with no `fundamentals` key on any stock entry, and every
 * fundamentalsRender.js helper returns ''/null so both prompt assemblers
 * render byte-identically to the pre-Fundamental-Wire text. That inertness
 * stays covered by the call-time flips in fundamentalsRender.flagOn.test.js.
 *
 * Reverting is as deliberate as the flip was: it must edit the ON-state
 * assertions in fundamentalsRender.test.js and regenerate the P4 battery
 * goldens in the SAME commit, exactly as the flip's reconciliation did.
 */
// Pinned by: fundamentalsRender.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const FUNDAMENTAL_MIRROR_ENABLED = true;

/**
 * Delight Layer Task 2 — the BATTLE-WEATHER STARFIELD on the DESKTOP dashboard
 * (spec V2, ruling R-T2-S5). A read-only ambient display layer: it writes
 * nothing, gates nothing, and is removable without touching any scoring or
 * decision path — the AGENT_PRESENCE_ENABLED / MATCHUPS_BACKDROP_ENABLED shape.
 *
 * FLIPPED false→true in the founder flag-flip PR #694 (after the A7 feel
 * sign-off). The merge-dark era is over: TRUE is now the default, and FALSE is
 * the deliberate-revert path.
 *
 * When TRUE (CURRENT): that ONE dashboard mount renders `StarfieldBackground`
 * instead of `DesktopBackground` (`App.jsx:8689`) and that ONE root paint
 * (`CommandDashboardDesktop`) becomes transparent so the field shows through.
 * `DesktopBackground.jsx` is NOT edited and keeps rendering on the other six
 * screens — so the app deliberately runs two ambient systems until the
 * everywhere-swap follow-on (spec V2 §7). The price lines are NOT deleted, which
 * is why the flip did not touch tokenGuardBaseline.json (R-T2-S6).
 *
 * When FALSE (revert path): the desktop dashboard is byte-identical to pre-flip —
 * `App.jsx:8689` mounts `DesktopBackground`, the root keeps its `CMD.bg` paint,
 * no loop is ever scheduled (`resolveLoopPlan` returns flag-off), and
 * `StarfieldBackground` is absent from the render tree.
 *
 * Reverting is as deliberate as the flip was: per BUILD_RULES §2 it MUST, in the
 * SAME commit, update the assertions that now pin the LIVE state — the value
 * pins in starfield.inert.test.jsx and this docstring.
 */
// Pinned by: starfield.inert.test.jsx (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const STARFIELD_BACKGROUND_ENABLED = true;

/**
 * The ONE home for the desktop starfield gate — the flag OR the `?starfield=1`
 * dev-preview override (the `?matchupsBackdrop=1` idiom). SSR/Node-safe (guards
 * `window`); a malformed URL degrades to the flag alone.
 */
export function isStarfieldOn() {
  if (STARFIELD_BACKGROUND_ENABLED) return true;
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('starfield') === '1';
  } catch {
    return false;
  }
}

/**
 * Delight Layer Task 2, Amendment A — the starfield on the MOBILE dashboard.
 *
 * DELIBERATELY INDEPENDENT of STARFIELD_BACKGROUND_ENABLED, and that
 * independence is the point: beta testers judge this on phones, so mobile must
 * be able to go dark on its own if a tester's device struggles, without
 * disturbing the desktop verdict. Never AND-gate the two.
 *
 * FLIPPED false→true in the founder flag-flip PR #694, on its own phone smoke
 * and separately from desktop (that independence is why it got its own flip).
 * The merge-dark era is over here too: TRUE is now the default, and FALSE is the
 * deliberate-revert path.
 *
 * When TRUE (CURRENT): `StarfieldBackground` renders on the mobile dashboard in
 * `mode="mobile"` — its own budget tier (fewer particles, DPR capped at 1.5),
 * not a shrunken desktop field — and the `CommandDashboard` root paint becomes
 * transparent so the field shows through. Because this puts NEW surface under
 * existing content rather than swapping an existing layer, card legibility at
 * peak intensity is the hard constraint that gated the mobile feel sign-off (A7).
 *
 * When FALSE (revert path): the mobile dashboard is byte-identical to pre-flip —
 * `App.jsx:8642` mounts `DesktopBackground` (which self-returns null on mobile —
 * mobile has no background layer at all in that state) and the `CommandDashboard`
 * root keeps its opaque `CMD.bg` paint.
 *
 * Reverting is as deliberate as the flip was: per BUILD_RULES §2 it MUST, in the
 * SAME commit, update the assertions that now pin the LIVE state — the mobile
 * value pin in starfield.inert.test.jsx and this docstring.
 */
// Pinned by: starfield.inert.test.jsx (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const STARFIELD_MOBILE_ENABLED = true;

/**
 * The ONE home for the mobile starfield gate — the flag OR `?starfieldMobile=1`.
 * SSR/Node-safe; malformed URL degrades to the flag alone.
 */
export function isStarfieldMobileOn() {
  if (STARFIELD_MOBILE_ENABLED) return true;
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('starfieldMobile') === '1';
  } catch {
    return false;
  }
}

/**
 * Delight Layer Task 2 — the DEV STATE OVERRIDE (ruling R-T2-S4).
 *
 * The feel-tuning instrument: `?warpState=resting|live|endgame` with an optional
 * `?warpClock=<seconds>` for where inside the endgame ramp to sit. Phase 0 found
 * ZERO house precedent for localStorage feature gating (the sole dev key,
 * `mc_api_debug`, toggles a debug monitor), so the spec's original localStorage
 * proposal was ruled dead in favour of this URL-param form — the same shape as
 * the five `isXxxOn()` helpers above.
 *
 * Honored only when the relevant starfield flag already resolves on, and it WINS
 * over live inputs. It does NOT drive a parallel display path: the override is
 * converted into the same `liveGames` shape the live adapter produces
 * (`synthesizeOverrideGames`), so it exercises the real state machine rather
 * than a look-alike that could drift from it.
 *
 * SSR/Node-safe. Returns null when absent or unrecognised — distinct from the
 * `resting` state, which is a real instruction to show the calm sky.
 *
 * @returns {{state: 'resting'|'live'|'endgame', clockSeconds: number|null}|null}
 */
export function getWarpDevOverride() {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('warpState');
    if (!raw) return null;

    const state = String(raw).trim().toLowerCase();
    if (state !== 'resting' && state !== 'live' && state !== 'endgame') return null;

    const rawClock = params.get('warpClock');
    const parsed = rawClock == null ? NaN : Number(rawClock);
    const clockSeconds = Number.isFinite(parsed) && parsed > 0 ? parsed : null;

    return { state, clockSeconds };
  } catch {
    return null;
  }
}

/**
 * Delight Layer Task 4 — the SIGNATURE DEPLOY (hold-to-deploy sky coupling).
 * Spec V1 decision D1. Basis:
 * docs/audits/20260801_DELIGHT_DEPLOY_SKY_COUPLING_PHASE0_DISCOVERY.md
 *
 * The room responds to your intent before you commit: while the user holds a
 * deploy button, the shipped hold gesture (`useHoldToDeploy`) dispatches
 * `ft-deploy-intent` and the starfield leans in — speed rises with hold
 * progress, exhales back on an early release, and (Phase 2) surges at commit.
 *
 * MERGED DARK at false. This is the Task-2 starfield's own merge-dark posture,
 * for the same reason: the whole feature is a FEEL change that only a founder
 * pass on the Vercel preview can judge (acceptance row A7).
 *
 * When FALSE (CURRENT): `useHoldToDeploy` dispatches NOTHING (the guard sits at
 * the dispatch helper, so every one of the six hold sites is covered at once)
 * and `StarfieldBackground` registers NO intent listener. The hold behaves
 * exactly as it shipped this week and the sky is driven by battle state alone —
 * byte-identical to today (acceptance row A1, pinned by
 * starfield.intent.test.jsx).
 *
 * When TRUE (flip path): the coupling is live wherever the hold already exists.
 * NOTE FOR THE FLIP PR (founder ruling R-T4-S1): flipping this ALSO makes the
 * "No battle live" card flip immediately on a successful deploy rather than up
 * to 120s late, because the Phase-2 settle appends the new battle to the shared
 * `activeAgentBattles` state. That is a truthfulness improvement, not a
 * regression — but it is a behaviour change OUTSIDE the sky and must be named
 * in the flip PR so it does not surprise a reviewer reading the diff.
 *
 * Flipping is as deliberate as the merge was: per BUILD_RULES §2/§11 the flip
 * PR MUST, in the SAME commit, update every assertion and docstring that pins
 * the pre-flip state — the value pins in starfield.intent.test.jsx and this
 * docstring.
 */
export const DEPLOY_SKY_COUPLING_ENABLED = true;

/**
 * The ONE home for the deploy-sky-coupling gate — the flag OR the
 * `?deploySkyCoupling=1` dev-preview override (the `?starfield=1` idiom).
 * SSR/Node-safe (guards `window`); a malformed URL degrades to the flag alone.
 *
 * This is the URL param the Phase-1 SOFT-STOP feel pass runs behind.
 *
 * Called per dispatch rather than latched at mount, deliberately: it is the
 * most direct guarantee that flag-off dispatches nothing (A1), with no window
 * where a cached value could disagree with the flag. Once the constant is true
 * the function returns on its first line, so the URL parse only ever runs in
 * the dark/preview state.
 */
export function isDeploySkyCouplingOn() {
  if (DEPLOY_SKY_COUPLING_ENABLED) return true;
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('deploySkyCoupling') === '1';
  } catch {
    return false;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSITION EVENT — PR 2: the one CLIENT-consumed flag. The three
// server-side composition flags live in api/_utils/compositionConfig.js (the
// masteryConfig.js precedent — endpoint test suites mock this file with
// explicit export lists). Table of record: docs/composition/PR2_FLAG_OWNERSHIP.md.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * D2 display: candidate greyed-with-reason on the compat copy surfaces
 * (compositionDisplay.js through compatSurfaceCopy). false — DEFAULT:
 * copy builders return their legacy output byte-identically (A23 test).
 */
// Pinned by: composition.acceptance.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const COMPOSITION_DISPLAY_ENABLED = false;

// ═══════════════════════════════════════════════════════════════════════════
// SPEC 1 — THE MANDATE (§7). All default false / safest, MERGE DARK.
// Spec 1 is HEADLESS (§1): in Phase 1 these gate server crons (registered in
// P6, no-op'ing while false) and the founder create endpoint; no client surface
// consumes them yet (MANAGED_MANDATE_ENABLED anticipates Spec 2+ client use).
// Flips are separate one-line PRs after a preview smoke — NEVER in a build PR;
// per BUILD_RULES §2/§11 a flip PR reconciles its pin (mandateFlags.test.js)
// and drops the flag's DARK_BY_DESIGN entry in the same commit.
// Spec: docs/SPEC1_MANDATE_SUBSTRATE_SPEC_V1_4.md §7.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * MASTER gate for The Mandate. When FALSE (DEFAULT, merge-dark) nothing
 * mandate-related runs: the eval/close/rollover crons no-op and the founder
 * create endpoint is closed regardless of the sub-flags below. The one switch
 * that keeps the whole substrate inert while it is built dark across Phases 1–6.
 */
// Pinned by: mandateFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const MANAGED_MANDATE_ENABLED = true;

/**
 * The scheduled evaluation loop (§3.1). When FALSE (DEFAULT), the
 * mandate-evaluate handler no-ops — no snapshot, no harvest, no submit. Built
 * P2; registered P6.
 */
// Pinned by: mandateFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const MANDATE_EVAL_ENABLED = true;

/**
 * The authoritative daily close pass (§3.6). When FALSE (DEFAULT), no book is
 * marked and no dailyRow is written by the close duty. Built P3. Close is the
 * sole writer of high-water marks / drawdown peaks (I6).
 */
// Pinned by: mandateFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const MANDATE_CLOSE_ENABLED = true;

/**
 * The rolling per-user rollover sweep (§5.3). When FALSE (DEFAULT), no book
 * crosses a quarter boundary. Built P4. Capital carries forward at rollover
 * (FR-1); the transaction asserts totalValue unchanged (I15).
 */
// Pinned by: mandateFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const MANDATE_ROLLOVER_ENABLED = false;

/**
 * Dormancy downshift (§6.5 / D-21). When FALSE (DEFAULT), no downshift derives.
 * Trading cadence and the daily close are NEVER downshifted — only future
 * reflection/narration depth (Spec 3). Built later.
 */
// Pinned by: mandateFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const MANDATE_DORMANCY_DOWNSHIFT_ENABLED = false;

/**
 * The founder-gated creation endpoint (api/mandate/create.js, §7). When FALSE
 * (DEFAULT), the endpoint returns 403 for everyone — even an allowlisted
 * founder uid — because a flag alone is not authorization AND authorization
 * alone is not the flag: creation requires BOTH this flag true AND the caller's
 * uid in MANDATE_FOUNDER_UIDS. This is the Phase 1 dark-testing switch. Also
 * gates the P4 accelerate and P5 drain founder-ops endpoints (the ambiguity-4
 * precedent: founder machinery, no new flag).
 */
// Pinned by: mandateFlags.test.js, drain.test.js (flagPinGuard: this value and the pins move together — BUILD_RULES §2).
export const MANDATE_FOUNDER_CREATE_ENABLED = true;

/**
 * Transport mode for mandate model calls (§3.3). 'direct' (DEFAULT, safest) or
 * 'batch' (production transport, built P5). A STRING enum, not a boolean gate,
 * so the flag-pin guard (which matches `*_ENABLED = true|false`) does not track
 * it — it is pinned directly in mandateFlags.test.js. A mode change takes effect
 * only after open batches drain (§3.3 drain protocol).
 */
export const MANDATE_TRANSPORT_MODE = 'direct';


/**
 * Exit-Behavior Rebalance Tier 2, Ask 3 — the profitTarget deterministic
 * executor (Founder Rulings V1 R1-R3/R10 + Addendum V1.1 R11). When FALSE
 * (DEFAULT, merge-dark): profitTarget stays a soft advisory note in
 * applyGuardrails, stays OUT of compileBuild's SUPPORTED_GUARDRAIL_SHAPES,
 * and the R11 suppression-path deterministic pass in agent-evaluate.js is a
 * no-op — byte-identical engine behavior. When TRUE, ONE flag lights all
 * three together (F11: compiler acceptance and executor registration can
 * never drift apart), and user-directive deterministic orders (equipped
 * stops + the profit target) fire through gameplan suppression (R11).
 * Flips WITH Ask 1's enforcement-true prompt copy per R10 — never alone,
 * never in a build PR.
 */
// Pinned by: agentGuardrails.pairing.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const PROFIT_TARGET_EXECUTOR_ENABLED = true;

/**
 * Metric History Snapshot Substrate (EXA_RETRIEVAL_INTEGRATION_SPEC_V1_4 §6.0 —
 * FOUNDER DECISION 2). ENABLED. After the ranking cron
 * (api/cron/compute-rankings.js) persists its ranking documents, it additionally
 * writes the daily per-ticker metric history (metricSnapshots/{ticker}/daily/{date})
 * and retains the raw quarterly series it already fetches transiently
 * (quarterlySeries/{ticker}) — detection substrate for Workstream B's decomposition
 * gate. Nothing reads this data yet; it is a failure-isolated, additive co-tenant
 * write (no new EODHD fetch, no new cron slot). Flipped true after the substrate
 * merge + Vercel preview smoke; instant rollback is set-false (which re-pins the
 * metricSnapshots.test.js off-state and restores this flag's DARK_BY_DESIGN entry).
 */
// Pinned by: metricSnapshots.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const METRIC_HISTORY_SNAPSHOT_ENABLED = true;

/**
 * Command Center Sync, Pass 1 (PASS1_SPEC_COMMAND_CENTER_SYNC_V1.md).
 * DARK — default FALSE.
 *
 * When TRUE, a live BaggerBomb battle turns the Dashboard from a
 * scoreboard-with-a-link into the surface that shows the agent's situation:
 * the derived phase (PRE_OPEN / LIVE / LIVE_CLOSED / POST_CLOSE), the Agent
 * Desk with score proximity and swap locks, the next-decision clock, and the
 * Huddle tab label in the battle view.
 *
 * READ-ONLY by construction. This flag gates rendering only — Pass 1 writes no
 * battle state, makes no model calls, and adds no listeners. When FALSE every
 * surface is byte-identical to today, which its render tests assert directly.
 *
 * Read it at RENDER scope, never module scope: 15 of 56 featureFlags vi.mock
 * call sites use a bare factory with no importOriginal spread, so a
 * module-scope const in a shared module resolves to undefined inside those
 * suites (the hazard documented in
 * docs/audits/20260819_EXIT_BEHAVIOR_ASK3_BUILD_REVIEW.md:120).
 *
 * The flip is its own deliberate PR after a founder preview smoke — never a
 * build PR. It updates the pin below AND drops the DARK_BY_DESIGN entry, in
 * that same commit.
 */
// Pinned by: commandCenterSyncFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const COMMAND_CENTER_SYNC_ENABLED = false;

/**
 * Archetype Rank Interface V2 (docs/specs/ARCHETYPE_RANK_INTERFACE_V2_BUILD_SPEC_V1_3.md).
 * DARK — default FALSE.
 *
 * When TRUE, computeArchetypeRankings (the §1-fenced scoring engine) dispatches
 * to the V2 pipeline in api/_utils/archetypeScoringV2.js: the axis vocabulary
 * (quality / strength / persistence / volatility / calm / dislocation),
 * deterministic pre-model filters, the bounded Diversifier sector interleave,
 * and the caller-owned game-mode blend (P-7). Every caller must then pass
 * opts.gameMode (P-5, fail closed) — the fenced decide.js:343 call gains its
 * explicit { gameMode: 'baggerBomb' } in the flip PR, never in a build PR.
 *
 * When FALSE (this build) the fenced engine is byte-identical to V1: the one
 * dispatch line returns null before a single V1 statement runs (the flag-off
 * byte-identity snapshot in archetypeScoring.v2dispatch.test.js asserts it).
 * The V2 module reads this flag at CALL time through a namespace import and
 * treats anything but `true` as off, so a suite that mocks this module with a
 * bare factory (no importOriginal) still takes the V1 path.
 *
 * The flip is its own §7 PR after the observation window passes spec §6
 * (≥ 5 market days of ops/rankingSnapshots; every archetype ≥ 35 post-filter
 * names on every snapshot). It updates the pin below AND drops the
 * DARK_BY_DESIGN entry in that same commit, plus the P-3 version bumps
 * (CALIBRATION_BUNDLE_VERSION 1→2, ARCHETYPE_IDENTITY_VERSION 3→4).
 */
// Pinned by: archetypeScoring.v2dispatch.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const ARCHETYPE_VECTORS_V2_ENABLED = false;

/**
 * BaggerBomb archetype hot-bench membership. DARK — default FALSE.
 * Read at CALL time by archetypeCandidateMenu.js. While false, refresh uses
 * the legacy fit list. Model presentation order remains composite order.
 * Flip in a separate founder PR after the real-data menu dry run in
 * docs/audits/BAGGERBOMB_ARCHETYPE_REFRESH_REVIEW_20261018.md §7.
 * Crons do not run on preview. The flip moves the pin and removes the
 * DARK_BY_DESIGN entry in flagPinGuard.test.js in the same commit.
 */
// Pinned by: archetypeCandidateMenu.test.js (flagPinGuard: value and pin move together — BUILD_RULES §2).
export const ARCHETYPE_REFRESH_MENU_ENABLED = false;

/**
 * Exit-Behavior Rebalance Tier 2, Ask 2 (rescoped) — equipped-rule precedence
 * made honest across the three non-archetype surfaces (kickoff 2026-09-01;
 * five founder decisions 2026-09-02). DARK — default FALSE.
 *
 * When TRUE, ONE flag lights all three together (the F11 one-flag discipline):
 *   1. the DECISION FRAMEWORK MUSTs the founder ruled qualifiable carry the
 *      "absent an equipped user rule" qualifier in both eval prompt variants
 *      (agentEvalPromptAssembly.js) — a framework default can no longer
 *      silently override the user's own equipped rule, the inversion Ask 1's
 *      four-layer precedence installed; the platform floors (LOCKED,
 *      ANTI-THRASH, SURVIVAL MODE, the distressed exclusion, the THRESHOLD
 *      PROXIMITY bonus band that IS the deterministic LOCK band) stay absolute;
 *   2. the R8 yield clause replaces the DR-13 subordination clause in every
 *      identity block (evalIdentityBlocks.js) — "equipped rules refine … but
 *      never reverse" contradicted the reconciler, which ranks user_equipped
 *      over archetype_default and drops the loser at decide.js:262 (an
 *      honesty fix; the DR-13 harness measured decision + symbols only, so no
 *      behavioral claim rides on it);
 *   3. the equip-time compiler DECLARES the profit target × mb-08 combination
 *      on the CompiledBuild and the compile preview (declaredConflicts; R8 /
 *      F10 — executor wins over the prompt-delegated veto). DOUBLY DARK: the
 *      compile itself runs only under COMPILER_ENABLED, so the declaration
 *      reaches a user only once THAT flag flips — its flip PR carries this
 *      declaration (registered in flagPinGuard's DARK_BY_DESIGN note).
 *
 * When FALSE (CURRENT), every prompt, every identity block and every compiled
 * build is byte-identical to the pre-Ask-2 output — golden-pinned against the
 * untouched tree at de4113fd (agentEvalPromptAssembly.ask2.test.js,
 * evalIdentityBlocks.ask2.test.js, compileBuild.ask2.test.js).
 *
 * Read it at CALL time, never module scope (the Ask 3 compileBuild lesson —
 * docs/audits/20260819_EXIT_BEHAVIOR_ASK3_BUILD_REVIEW.md §8); compileBuild()
 * itself stays pure and receives the read as an input from the equip-time
 * caller, inside its enabled path only. Flips after Asks 1+3 (live) per R10,
 * in its own one-line PR after founder smoke — never a build PR. THE FLIP
 * MAP (review B-F4, the Ask 1 §4 precedent): that flip updates the pin
 * below AND drops the DARK_BY_DESIGN entry, in one commit, plus the
 * mechanical snapshot regens — `npx vitest run
 * api/_utils/p4Equivalence.battery.test.js
 * api/_utils/evalIdentityBlock.injection.test.js -u` (2 P4 + 12 DR-13
 * flag-on prompt snapshots re-capture the qualified MUSTs and the yield
 * clause; review that diff — it must be exactly the ruled prose delta).
 * Nothing else: evalIdentityBlocks.test.js branches on the live clause,
 * and evalIdentityBlocks.flagOn.test.js + agentEvalPromptAssembly.ask1.test.js
 * pin the Ask-2-dark branch (they test DR-13 / Ask 1, not this arc).
 */
// Pinned by: equippedRulePrecedenceFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const EQUIPPED_RULE_PRECEDENCE_ENABLED = false;

/**
 * Battle View controller, Phase A (docs/design/PHASE_A_RULINGS_AND_AMENDMENTS_V1.md,
 * ledger D-59 → D-90 in docs/audits/COMMAND_CENTER_BATTLE_SYNC_DESIGN_FRAMEWORK_V1_2.md).
 * LIVE — default TRUE since the flip PR (September 4, 2026).
 *
 * The live-battle screen (AgentBattleScreen) plays as a controller: the score
 * header carries the turn line (`Checked 12:47 PM · next ~1:02 PM`, from the
 * same adapter arithmetic the Desk ships), the Matchups rows read the
 * subscribed battle doc rather than the frozen prop (D-59), every player row
 * opens Why? — the agent's own persisted words from the last decision — and
 * the chat's directive cards carry truthful receipts (`Filed · Replaced ·
 * Expired`, D-51 / D-60) in place of the shipped execution promise.
 *
 * READ-ONLY by construction. Phase A writes nothing to the battle doc, makes
 * no model call, adds no listener and never touches api/. The ONE change
 * outside this flag is D-62: the dashboard Desk's closed-phase string
 * (DESK_COPY.postureClosed) carries the as-of stamp too — one sentence on
 * both surfaces — and that string is gated by the Desk's own
 * COMMAND_CENTER_SYNC_ENABLED (still dark), never by this flag.
 *
 * ROLLBACK IS THIS ONE LITERAL. Setting it back to false restores the shipped
 * tabbed screen byte-for-byte: the flag-off path is not legacy debris, it is a
 * guarded fallback. Four suites force it off through a vi.mock of the accessor
 * — AgentBattleScreen.flagOff.golden, .flagOff.tape.jsdom, .restFallback and
 * __golden__/captureFlagOffGolden — and two committed HTML goldens hold that
 * output to the byte. Those suites guarded the DEFAULT before the flip and
 * guard the FALLBACK after it; nothing about them changed, which is the point.
 *
 * INDEPENDENT of COMMAND_CENTER_SYNC_ENABLED (the dashboard Desk): neither
 * flag reads the other. The Desk stays dark; this flip says nothing about it.
 *
 * Read it at RENDER scope through isBattleViewControllerOn() below, never as
 * a module-scope const (the Pass 1 hazard: 15 of 56 featureFlags vi.mock
 * sites use a bare factory with no importOriginal spread).
 */
// Pinned by: battleViewControllerFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const BATTLE_VIEW_CONTROLLER_ENABLED = true;

/**
 * The ONE home for the Battle View controller gate — now the flag, and nothing
 * else.
 *
 * The `?battleViewController=1` preview override (smoke Pattern B, Phase A
 * rulings §1.4) is GONE: it existed so the founder could look at the
 * dark-merged controller on a Vercel preview before the production flip, and
 * it was deleted in the SAME COMMIT that flipped the flag — flip, pin and
 * override travel together, which is the `?fuseHero=1` precedent and the
 * `?leagueLiveOrb=1` lesson behind it (that one left a URLSearchParams read
 * wired to nothing, and it sat here for months because nobody could tell
 * whether it was load-bearing).
 *
 * The accessor SURVIVES the flip (the ARENA_LIVE_ON / FUSE_HERO_ON precedent)
 * as the flag's one consumer seam: AgentBattleScreen calls this at render
 * scope, never the constant, so a test can mock either state without touching
 * module scope and the rollback stays one literal above.
 *
 * A source row in battleViewControllerFlags.test.js asserts no query-string
 * read has crept back — the only instrument that can still fail here, since a
 * behavioural row cannot observe an override the live flag short-circuits.
 */
export function isBattleViewControllerOn() {
  return BATTLE_VIEW_CONTROLLER_ENABLED;
}

/**
 * Battle View character pane — Phase A3, the character IS the controller
 * (D-91). The agent's mark stops being decoration on the score header and
 * becomes the one door to the conversation: it floats on the board, it speaks
 * once when the tape gains an entry, and it opens a pane holding Chat, Bench
 * and Tape. The bottom strip and the three-detent sheet retire behind it
 * (D-93, superseding D-74).
 *
 * LIVE (DEFAULT true) since the flip, in its own one-line PR after the
 * founder's A3.5 preview smoke on `smoke/character-pane`. FALSE is now the
 * ROLLBACK, and it is one literal above: with it false the Battle View renders
 * Phase A2 exactly as merged — the desktop PeekStrip column, the mobile
 * ChatSheet, the header's Game Tape link and watchlist chip, the global bug
 * widget. Every A3 branch is `paneOn ? <new> : <the A2 JSX as merged>`, so
 * pane-off stays byte-identical and keeps its own golden
 * (agentBattleScreen.controllerOn.paneOff.html), captured from the pre-build
 * commit 8e63ea65 and asserted through a forced-false mock — so the flip leaves
 * that file guarding the fallback rather than following the flag to the pane.
 *
 * NESTED on the controller, deliberately: the pane replaces containers the
 * controller introduced, so it can only mean anything where the controller is
 * live. Reading the pane flag alone would light a pane over the shipped tabbed
 * screen, which has no board column to float an avatar on.
 *
 * No query-string override. The controller's `?battleViewController=1` was
 * deleted in the same commit that flipped it (the `?fuseHero=1` precedent, and
 * the `?leagueLiveOrb=1` lesson behind it); this runway does not re-open that
 * door. The founder's A3.5 smoke used the Phase 0 §7 pattern instead.
 *
 * Read it at RENDER scope through isCharacterPaneOn() below, never as a
 * module-scope const (the Pass 1 hazard: 15 of 56 featureFlags vi.mock sites
 * use a bare factory with no importOriginal spread).
 */
// Pinned by: characterPaneFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const BATTLE_VIEW_CHARACTER_PANE_ENABLED = true;

/**
 * The ONE home for the character-pane gate: the controller AND the pane flag.
 *
 * The conjunction lives here rather than at the call sites so the screen reads
 * one accessor beside isBattleViewControllerOn() and a test can mock either
 * state without reconstructing the nesting.
 */
export function isCharacterPaneOn() {
  return isBattleViewControllerOn() && BATTLE_VIEW_CHARACTER_PANE_ENABLED;
}

/**
 * Voice-layer grounding — the narrator gains INPUTS, not authority
 * (docs/design/VOICE_LAYER_GROUNDING_SPEC_V1_2.md §9; the Sep 7 rulings,
 * docs/audits/20260907_VOICE_GROUNDING_BUILD_PHASE0_REPORT.md §4 items 2–4).
 *
 * A STRING tri-state, not a boolean gate — the ARCHETYPE_INTEGRITY_MODE /
 * SECTOR_CAP_MODE house shape (ruling D1), walked by the FOUNDER, never in a
 * build PR. What each state guarantees:
 *
 *   'off'     — every prompt byte-identical to the pre-grounding assembly.
 *               voiceLayerPrompt.grounding.goldens.test.js holds the bytes,
 *               captured from 70ba90a1 (the post-ATR-fix commit) and asserted
 *               against every surface the arc touches.
 *   'shadow'  — chat.js assembles BOTH prompts, SENDS THE OLD ONE, and logs
 *               both (with the history and this mode) to shadow/conversations.
 *               Proves assembly and token size on real battles; nothing else.
 *   'canary'  — the NEW prompt is sent to the uids in the environment's
 *               VOICE_GROUNDING_CANARY_UIDS (comma-separated; unset or empty
 *               = NOBODY — it fails closed); everyone else stays on the
 *               'shadow' behaviour. The uid list is an ENV VAR and never a
 *               repo constant — the MANDATE_FOUNDER_UIDS precedent
 *               (api/mandate/create.js:18-20): the founder's uid stays out of
 *               the repo, and this file is imported by the client bundle.
 *   'on'      — the new prompt for everyone.
 *
 * THE PIN. The flag-pin guard (flagPinGuard.test.js) tracks only
 * `*_ENABLED = true|false`, so a string enum is invisible to it and CANNOT
 * sit in DARK_BY_DESIGN (its integrity test fails any key outside that map).
 * This flag is therefore pinned DIRECTLY in voiceGroundingFlags.test.js — the
 * MANDATE_TRANSPORT_MODE precedent — with no boolean companion (ruling 3).
 * Each walk step ('off' → 'shadow' → 'canary' → 'on') reconciles that pin in
 * its own one-line PR (BUILD_RULES §2). Rollback is the same literal, walked
 * back.
 *
 * CURRENT STATE — 'shadow' (walk step 1, from 'off'; Sep 8 2026). Every caller
 * still RECEIVES the shipped prompt, so the sent bytes are the off goldens;
 * what changes is that chat.js assembles the grounded prompt beside it and both
 * ride the shadow record (chat.js:416 gates that on `!== 'off'`). ONE non-chat
 * surface reads `!== 'off'` too and comes alive at this step, by design: the
 * voice-layer cache cron mirrors `fundamentals` onto the cached briefs
 * (§3.5, voice-layer-cache.js:819 — the shipped prompt ignores the field, which
 * is why the off goldens' fixture carries one). POST /api/agent/file-directive
 * does NOT come alive here: its gate compares `=== 'on'`, the same resolution
 * that mints the chips (chat.js:409), so the filing surface and the minting
 * surface walk together rather than the route standing open at 'shadow' with
 * nothing minted to file. Everything else compares `=== 'on'` too and is
 * unchanged here.
 *
 * Read it at CALL time through getVoiceGroundingMode(uid) below, never as a
 * module-scope const in a consumer (the isCharacterPaneOn rule: many
 * featureFlags vi.mock sites use a bare factory with no importOriginal
 * spread).
 */
// Pinned by: voiceGroundingFlags.test.js (a STRING enum — pinned directly, outside flagPinGuard's `*_ENABLED` scan; this value and the pin move together — BUILD_RULES §2).
export const VOICE_GROUNDING_MODE = 'shadow';

/** The four founder-walked states, in walk order. */
export const VOICE_GROUNDING_MODES = Object.freeze(['off', 'shadow', 'canary', 'on']);

/**
 * The canary allowlist, parsed from the raw env value at CALL time (never
 * cached, never a constant): comma-separated uids, trimmed, empties dropped.
 * Unset / empty / whitespace → [] → nobody. Pure and exported so the
 * fail-closed property is directly testable without touching process.env.
 */
export function parseVoiceGroundingCanaryUids(raw) {
  if (typeof raw !== 'string') return [];
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
}

/**
 * The EFFECTIVE grounding mode for one caller, as a pure function of the
 * flag, the caller's uid and the raw canary list. 'canary' never leaves this
 * function: it resolves to 'on' for an allowlisted uid and to 'shadow' for
 * everyone else (the old prompt is still what they receive; assembly of the
 * new one keeps being measured). A mode outside VOICE_GROUNDING_MODES — a
 * typo on the walk — resolves to 'off', the only state that changes nothing.
 *
 * @param {string} mode        the flag value
 * @param {string|null} uid    the authenticated caller (from the token, never
 *                             the body); null / undefined never matches
 * @param {string|undefined} rawCanaryUids  process.env.VOICE_GROUNDING_CANARY_UIDS
 * @returns {'off'|'shadow'|'on'}
 */
export function resolveVoiceGroundingMode(mode, uid, rawCanaryUids) {
  if (mode === 'canary') {
    const allow = parseVoiceGroundingCanaryUids(rawCanaryUids);
    return typeof uid === 'string' && uid && allow.includes(uid) ? 'on' : 'shadow';
  }
  if (mode === 'shadow' || mode === 'on') return mode;
  return 'off';
}

/**
 * The ONE home for the grounding gate — read at CALL time, per caller.
 *
 * The env read happens INSIDE the call (ruling 2: "parsed at call time inside
 * getVoiceGroundingMode(uid)"), so a deploy that sets the variable takes
 * effect on the next request with no module reload, and a runtime without
 * `process` (this module is in the client bundle; the client never needs this
 * accessor) reads an empty list rather than throwing.
 *
 * @param {string|null} uid  the authenticated caller's uid
 * @returns {'off'|'shadow'|'on'}
 */
export function getVoiceGroundingMode(uid) {
  // globalThis.process: undefined in the browser bundle (this file is client
  // code too), the real process on the server — no `process` global is named.
  const raw = globalThis.process?.env?.VOICE_GROUNDING_CANARY_UIDS;
  return resolveVoiceGroundingMode(VOICE_GROUNDING_MODE, uid, raw);
}

/**
 * Phase B — THE TICK STAMPS (docs/design/PHASE_B_TICK_STAMPS_SPEC_V1.md §1;
 * discovery docs/audits/20260908_PHASE_B_TICK_STAMPS_PHASE0_DISCOVERY.md;
 * ledger D-110 → D-113). ONE server flag for the three stamps the evaluation
 * cron writes onto every decided check's own `evaluations[]` entry, AFTER the
 * decision, from objects the tick already holds (api/cron/agent-evaluate.js,
 * the entry composition; the pure composer is api/_utils/tickStamps.js):
 *   heard      — { directiveThreadId, suppressed } from the cron's own
 *                resolveControls call on the in-memory battle: the thread was
 *                in the decider's prompt at this check (never that it acted).
 *   evidence   — nine fields per held position + one vintages block per entry:
 *                what the decider saw at that check, code-composed.
 *   candidates — the decider's own anticipation output, four fields plus tag.
 * None changes a decision; the fenced decider reads a fixed whitelist of the
 * entry (formatRecentEvals) and is inert to the new keys (pinned).
 *
 * LIVE since the flip (D-113): B1 merged dark and the founder flipped it here,
 * in its own PR, so the first stamped check on production is the smoke (crons
 * do not run on preview). FALSE is now the ROLLBACK, one literal on the export
 * below: with it false the composed entry is BYTE-IDENTICAL to the pre-B1 entry
 * (the golden in agent-evaluate.tickStamps.flagOff.test.js) and no extra work
 * runs at the stamp site. Clients render on PRESENCE of the keys, never on this
 * flag — nothing client-side reads it, so no client file moved at the flip.
 *
 * Read at CALL time at the ONE splice in agent-evaluate.js (`if
 * (TICK_STAMPS_ENABLED && promptBuilt)` inside processAgentBattle, inside the
 * stamp block's fail-safe — never a module-scope derivation), so a hermetic
 * featureFlags mock with an explicit value governs every test tick. A
 * bare-factory mock that OMITS the name does not resolve to off: under vitest a
 * missing export on a factory mock THROWS on access (the v2dispatch.bareMock
 * precedent), so every suite that drives processAgentBattle spreads
 * `importOriginal` in its featureFlags mock — tickStampsFlags.test.js pins that
 * for the cron suites — and the read sits inside the fail-safe so even such a
 * throw could never cost a tick its write (review C-1 / B-2).
 *
 * FLIP MAP (the flip PR reconciles these in the SAME commit — BUILD_RULES §2):
 *   • src/config/tickStampsFlags.test.js — the dark pin row moves to true;
 *   • src/config/flagPinGuard.test.js — drop TICK_STAMPS_ENABLED from
 *     DARK_BY_DESIGN (its integrity test reds if a lit flag is left listed).
 *   The flag-off golden suite mocks this flag to an explicit false and does
 *   NOT move; the flag-on suite mocks it true and does not move either.
 */
// Pinned by: tickStampsFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const TICK_STAMPS_ENABLED = true;

/**
 * PHASE C — SHOW IT (spec V1 §6 / V1.1 ruling 4, ledger D-116 → D-122).
 *
 * ONE flag for the second verb: the research path. When it resolves ON —
 *   · POST /api/agent/research answers (it 404s while this is false, so the
 *     route does not exist as far as any caller is concerned);
 *   · a `{ kind: 'research', symbol }` chip survives normalizeSuggestedActions
 *     and the grounded OUTPUT_FORMAT offers the model the third kind;
 *   · the Why? panel's third door, the Bench chip's door and the conversation's
 *     research chip render.
 * When it is false every one of those is absent — no chip label, no door, no
 * route, and the grounded prompt is byte-identical to today's.
 *
 * FALSE at merge (V1.1 §E): Phase C merges dark and the founder flips it in its
 * own one-line PR after the build merges. Vercel preview is the smoke surface;
 * production exists only after the founder merges and deploys.
 *
 * Read at CALL time inside the function that gates on it — never a module-scope
 * derivation — so a hermetic featureFlags mock with an explicit value governs
 * every test (the TICK_STAMPS_ENABLED / isCharacterPaneOn rule).
 *
 * NOT a VOICE_GROUNDING_MODE gate. The research card is code-composed platform
 * data with no model call, so it does not wait on the voice walk; the narrator's
 * PLATFORM RESEARCH block is grounded-only by construction (it is assembled only
 * on the grounded path) and is inert while no research exchange exists.
 *
 * FLIP ORDER — READ THIS BEFORE FLIPPING (review B-5). The card is code-composed
 * platform data and needs no narrator, so this flag is deliberately independent
 * of VOICE_GROUNDING_MODE. But the FOLLOW-UP is not: the PLATFORM RESEARCH block,
 * the research rule and the reply lint all ride the GROUNDED prompt, and
 * `grounded` is false for every caller while the mode is 'shadow'. Flipping this
 * alone therefore ships the card and its doors WITHOUT the D-121 machinery — the
 * character cannot see the card at all (both history builders exclude it
 * structurally, so it honestly says it has no data on the name), but it cannot
 * discuss it either. The intended sequence is VOICE_GROUNDING_MODE -> 'on'
 * first, then this flag. Flipping earlier is a product limitation, not a
 * dishonesty.
 *
 * FLIP MAP (the flip PR reconciles these in the SAME commit — BUILD_RULES §2):
 *   • src/config/showItFlags.test.js — the dark pin row moves to true;
 *   • src/config/flagPinGuard.test.js — drop SHOW_IT_ENABLED from
 *     DARK_BY_DESIGN (its integrity test reds if a lit flag is left listed).
 *   The flag-off darkness suite (api/agent/research.dark.test.js) mocks this
 *   flag to an explicit false and does NOT move.
 */
// Pinned by: showItFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const SHOW_IT_ENABLED = false;

/**
 * The ONE client-side gate for the Show it doors (Phase C §1 / §6).
 *
 * The doors live on the Battle View controller's surfaces — the Why? panel and
 * the Bench pane — so the gate is the controller AND the flag, the conjunction
 * living here beside isCharacterPaneOn() rather than at the call sites. Read at
 * RENDER scope through this accessor, never as the bare constant: many
 * featureFlags vi.mock sites use a bare factory with no importOriginal spread,
 * and a missing export THROWS on access under vitest.
 *
 * The SERVER does not go through here — the route and the chip normalizer read
 * SHOW_IT_ENABLED directly, at call time, because neither has a controller.
 */
export function isShowItOn() {
  return isBattleViewControllerOn() && SHOW_IT_ENABLED;
}

/**
 * BACKING BETA — PR 0: ELIGIBILITY ATTESTATION (spec V1.3 §5 / §6 / §8 / §12
 * PR 0; ruling D-z). A PLATFORM PRIMITIVE, not a backing feature: the first
 * age, terms or consent state the platform has ever held (addendum §2 Q3 —
 * nothing existed). `users/{uid}` is owner-writable, so the attestation lives
 * in its own server-written `eligibility/{uid}` doc under a `write: if false`
 * rule (the tournamentRanks pattern); it is an attestation, not verification.
 *
 * ONE flag for the ONE door. When it resolves ON —
 *   · POST /api/eligibility/attest answers (it 404s while this is false, AFTER
 *     auth — the SHOW_IT_ENABLED / research.js shape — so the route does not
 *     exist as far as any caller is concerned).
 * That route is the only thing this flag governs. While it is false the route
 * is absent, and nothing else is reachable either: PR 0 ships no UI (the
 * AttestationStep lands in PR 4 at the backing entry), no existing endpoint
 * calls the read helper (api/_utils/eligibility.js — its first caller is PR 2),
 * and the `eligibility` rules block is inert until deployed via the Console.
 *
 * FALSE at merge (§11 gates 1–2, §12): PR 0 merges dark. Before the flip,
 * counsel's copy replaces the two `COUNSEL: replace before flip` placeholders
 * in src/constants/eligibility.js and TERMS_VERSION becomes the ratified
 * version (its COPY MAP names the rows that move; eligibilityFlags.test.js
 * reds a lit flag that still carries either); the rules block is deployed.
 * The flip PR then flips this flag TOGETHER WITH BACKING_BETA_ENABLED after
 * every §11 gate — never a build PR.
 *
 * Read at CALL time inside the handler that gates on it — never a module-scope
 * derivation — so a hermetic featureFlags mock with an explicit value governs
 * every test (the TICK_STAMPS_ENABLED / SHOW_IT_ENABLED rule). No accessor:
 * the server reads the bare constant, and there is no client gate in PR 0.
 *
 * THE FOUNDER SMOKE OVERRIDE (the activation PR; spec V1.3 §11 gate 4 as
 * amended by Amendment A §A7): on a Vercel PREVIEW only, with
 * BACKING_SMOKE_ENABLED=true and the caller's verified uid in
 * BACKING_SMOKE_UIDS, the attestation door reads lit for THAT uid through
 * api/_utils/backingSmoke.js (`eligibilityLitFor(uid)` = this constant OR the
 * override). Production ignores it entirely; this constant does not move, and
 * the counsel-copy tripwire below keys on the constant, so it cannot fire from
 * the override. The flip PR DELETES the override (the ?fuseHero=1 precedent).
 *
 * FLIP MAP (the flip PR reconciles these in the SAME commit — BUILD_RULES §2):
 *   • src/config/eligibilityFlags.test.js — the dark pin row moves to true;
 *   • src/config/flagPinGuard.test.js — drop ELIGIBILITY_ATTESTATION_ENABLED
 *     from DARK_BY_DESIGN (its integrity test reds if a lit flag is left listed).
 *   The flag-off darkness suite (api/eligibility/attest.dark.test.js) mocks
 *   this flag to an explicit false and does NOT move; the lit suite
 *   (api/eligibility/attest.test.js) mocks it per row and does not move either.
 */
// Pinned by: eligibilityFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const ELIGIBILITY_ATTESTATION_ENABLED = false;

/**
 * BACKING BETA — PR 1–5: THE BACKING LAYER (spec V1.3 §0, §2, §4, §12; the
 * whole rulings ledger D-a…D-z). A points-only spectator layer over base-layer
 * League group-weeks: a weekly allowance of Backing Points staked on the pods
 * you are not seated in, one sealed parimutuel pot per pod, settled off the
 * composite the tournament already computes. No money, no crypto, no
 * purchasable or redeemable points, no rake, no carry, no public ranking
 * (§14). Points are score, never a bankroll (§2).
 *
 * ONE flag for every backing door. PR 1 ADDS NO DOOR — it is the foundation
 * the later PRs compose: constants, the week helper, the wallet/ledger
 * primitives, the rules blocks and the two stake indexes. Nothing in PR 1
 * reads this flag, nothing calls the helpers, and no collection receives a
 * document. The doors arrive with their PRs and are listed here as they land:
 *   · PR 1 — NONE (this PR). Foundation only; no endpoint, no UI, no caller.
 *   · PR 2 — POST /api/tournament/backing-stake, GET /api/tournament/backing-pools.
 *   · PR 3 — POST /api/tournament/backing-settle (admin re-run) + the
 *     settlement hook's own gate.
 *   · PR 4 — the backing surfaces under src/components/League/backing/,
 *     GET /api/tournament/team-card and POST /api/team/pitch.
 *   · PR 5 — GET /api/backing/results (the settle-on-read host), GET
 *     /api/backing/my-stats, GET /api/backing/trainer-stats, POST
 *     /api/backing/event; the admin re-run's `action: 'refund'`.
 *   · The pre-flip cleanup (Amendment C) — GET /api/backing/team-labels, the
 *     server's names for Your Backing's teams (D-af).
 * Every server route 404s while this is false, AFTER auth (the
 * SHOW_IT_ENABLED / research.js shape), so no route exists as far as any
 * caller is concerned.
 *
 * FALSE at merge (§11, §12): PR 1–5 all merge dark. The flip PR flips this
 * flag TOGETHER WITH ELIGIBILITY_ATTESTATION_ENABLED after every §11 gate
 * (counsel's jurisdictional review, the attestation deployed with counsel's
 * copy, the two honesty fixes, the founder preview smoke, the deferral watch,
 * and N1 mitigated per Amendment A §A1) — never a build PR.
 *
 * Read at CALL time inside the handler that gates on it — never a module-scope
 * derivation — so a hermetic featureFlags mock with an explicit value governs
 * every test (the TICK_STAMPS_ENABLED / SHOW_IT_ENABLED rule). No accessor.
 *
 * THE FOUNDER SMOKE OVERRIDE (the activation PR; §11 gate 4 as amended by
 * Amendment A §A7): every USER route above reads `backingLitFor(uid)`
 * (api/_utils/backingSmoke.js) — this constant OR, on a Vercel PREVIEW only,
 * with BACKING_SMOKE_ENABLED=true and the caller's verified uid in
 * BACKING_SMOKE_UIDS, the override for that uid — and every client surface
 * reads the server's answer to GET /api/backing/lit (src/hooks/useBackingLit.js).
 * A smoke session is listed and may back DEV pods only. Production ignores the
 * override entirely; this constant does not move. The admin re-run and the
 * Friday duty hook have no caller uid and keep the bare constant. The flip PR
 * DELETES the override with the two env vars (the ?fuseHero=1 precedent).
 *
 * FLIP MAP (the flip PR reconciles these in the SAME commit — BUILD_RULES §2):
 *   • src/config/backingBetaFlags.test.js — the dark pin row moves to true;
 *   • src/config/flagPinGuard.test.js — drop BACKING_BETA_ENABLED from
 *     DARK_BY_DESIGN (its integrity test reds if a lit flag is left listed).
 *   PR 1 adds no flag-off darkness suite because PR 1 adds no door: there is
 *   no behavior to hold byte-identical. The per-PR dark suites (PR 2's
 *   backing-stake.dark.test.js onward; PR 5's backing-routes.dark.test.js for
 *   the api/backing/ routes, five since the pre-flip cleanup) mock this flag to
 *   an explicit false and do NOT move with the flip.
 */
// Pinned by: backingBetaFlags.test.js, backing-stake.dark.test.js, backing-routes.dark.test.js (flagPinGuard: this value and the pins move together — BUILD_RULES §2).
export const BACKING_BETA_ENABLED = false;

/**
 * THE THRESHOLD LINT — no promise on a signal the tick did not hold
 * (docs/audits/20260915_PHASE0_SIGNAL_LANGUAGE.md §5 + §7.2 shape 2; the
 * pure module is api/_utils/anticipationThresholdLint.js).
 *
 * A STRING TRI-STATE, walked in the grounding walk's own pattern — measure
 * before enforcing:
 *   'off'    the lint is not called. Today's path, byte-identical: every
 *            candidate the decider emits reaches the anticipation queue and
 *            the evaluations[].candidates[] stamp exactly as it does now.
 *   'shadow' every candidate is linted and nothing is dropped. A candidate
 *            that WOULD fail is logged to the anticipation shadow stream with
 *            errorStep 'threshold_absent_signal' and its `absent` list — the
 *            measurement the founder reads before enforcing.
 *   'on'     a failing candidate is DROPPED from both persistence sites — it
 *            reaches neither Gemma nor the candidates[] stamp — and logged.
 *
 * REJECT, NEVER REWRITE: an accepted threshold is byte-identical to what the
 * decider wrote. A dropped candidate is simply not a fact on the record; the
 * `heard` / `saw` verbs and the stamp composer are untouched (the stamp's
 * INPUT shrinks under 'on', tickStamps.js does not change).
 *
 * The flip to 'shadow' is its own one-line PR; the flip to 'on' another —
 * never a build PR. Each flip moves the pin row in
 * src/config/anticipationThresholdLintFlags.test.js in the SAME commit
 * (BUILD_RULES §2) and updates the note in flagPinGuard.test.js's
 * DARK_BY_DESIGN block, which cannot HOLD this flag: the guard scans
 * `*_ENABLED = true|false` and its integrity test rejects any key outside
 * that boolean map. The VOICE_GROUNDING_MODE / MANDATE_TRANSPORT_MODE
 * precedent — a string tri-state pins directly.
 *
 * Read as a module constant in the cron, like TICK_STAMPS_ENABLED beside it;
 * there is no per-uid resolution to make, so there is no accessor.
 */
// Pinned by: anticipationThresholdLintFlags.test.js (a STRING tri-state — pinned directly, outside flagPinGuard's `*_ENABLED` scan; this value and the pin move together — BUILD_RULES §2).
export const ANTICIPATION_THRESHOLD_LINT_MODE = 'off';

/** The three founder-walked states, in walk order. */
export const ANTICIPATION_THRESHOLD_LINT_MODES = Object.freeze(['off', 'shadow', 'on']);

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * INTRADAY DATA — BUILD 1 (contract docs/specs/INTRADAY_DATA_BUILD_1_CONTRACT_V1_1.md
 * §3; discovery docs/audits/20260918_PHASE0_INTRADAY_DATA.md + _ADDENDA.md;
 * build report docs/audits/20260919_BUILD1_INTRADAY.md). Five flags, ALL OFF
 * at merge. With every flag off every surface is byte-identical to today
 * except the §11 legacy-gate freshness fix, which ships with flags off by
 * contract and is stated in the PR body.
 *
 * INTRADAY_COLLECT_ENABLED — the per-minute delayed-quote poller
 * (api/cron/intraday-poll.js) and the next-day validator
 * (api/cron/intraday-validate.js) RUN. Off → both handlers exit 200 with
 * `{ skipped: true, reason: 'flag_off' }` before any read, any vendor call
 * or any write. Read as a module constant in the two crons. FLIPPED TRUE on
 * 2026-09-21 by `39e5c48a`, after the founder's day-1 smoke (§13) passed:
 * one line, this file, with the pin row in src/config/intradayFlags.test.js
 * moved in the same commit (BUILD_RULES §2).
 */
// Pinned by: intradayFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const INTRADAY_COLLECT_ENABLED = true;

/**
 * INTRADAY_DIAGNOSTIC_ENABLED — the evaluator reads intradaySnapshots/latest
 * once per invocation and writes agentBattles/{id}/intradayViews/{evalId}
 * per battle per tick (§8.1, isolated: bounded to 2 s, non-fatal, the
 * trading evaluation and its authoritative battle write proceed unchanged);
 * the evaluation entry carries the eight pointer fields (§8.1) and the
 * vintages block gains intradaySnapshotId / intradayGeneration (§8.4);
 * the player's Why? panel fetches the view on open and renders the
 * diagnostic block (§9.1); shadow lines are STORED on the view, never sent.
 * Off → no snapshot read, no view write, no pointer field, the vintages
 * block byte-identical to today, no client fetch. Read as a module constant
 * in api/cron/agent-evaluate.js (the TICK_STAMPS_ENABLED shape) and at
 * render scope on the client. The sent prompt is identical on and off
 * (§9.2 (d), asserted).
 */
// Pinned by: intradayFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const INTRADAY_DIAGNOSTIC_ENABLED = false;

/**
 * INTRADAY_AGENT_USE_ENABLED — STAGE 2 (deferred): the agent's decision may
 * use eligible intraday evidence. PRESENT, NO CONSUMER in build 1 except the
 * threshold lint (§9.3): the five intraday-timeframe signal names become
 * present only when a view exists AND this is true — false → never present,
 * exactly today's behaviour. Gate: the paired-eval scorecard (§2). Registered
 * DARK_BY_DESIGN in flagPinGuard.test.js; a deliberate flip drops that entry.
 */
// Pinned by: intradayFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const INTRADAY_AGENT_USE_ENABLED = false;

/**
 * INTRADAY_PRICE_SOURCE — STAGE 3 (deferred): 'legacy' (today's
 * /real-time/ quote) | 'snapshot' (the §8.5 adapter over
 * intradaySnapshots/latest). PRESENT, NO CONSUMER in build 1; the adapter
 * `toLegacyPriceShape` exists and is tested. A STRING enum, so the flag-pin
 * guard (which scans `*_ENABLED = true|false`) cannot see it: pinned
 * directly in src/config/intradayPriceSourceFlags.test.js, the
 * ANTICIPATION_THRESHOLD_LINT_MODE precedent. Gate: G11/G12 and ID-22 §7.
 */
// Pinned by: intradayPriceSourceFlags.test.js (a STRING enum — pinned directly, outside flagPinGuard's `*_ENABLED` scan; this value and the pin move together — BUILD_RULES §2).
export const INTRADAY_PRICE_SOURCE = 'legacy';

/** The two price sources, in adoption order. */
export const INTRADAY_PRICE_SOURCES = Object.freeze(['legacy', 'snapshot']);

/**
 * INTRADAY_RISK_ACTIVATION_ENABLED — STAGE 4 (deferred): the risk layer may
 * act on eligible, closeQualified intraday evidence via `evaluateIntraday`
 * run immediately before every action. PRESENT, NO CONSUMER in build 1.
 * Gate: the §10.6 qualification calendar (10 characterisation sessions, a
 * frozen policy, 10 fixed test sessions). Registered DARK_BY_DESIGN in
 * flagPinGuard.test.js; a deliberate flip drops that entry.
 */
// Pinned by: intradayFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const INTRADAY_RISK_ACTIVATION_ENABLED = false;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TICK CAPTURE — the per-check observation record (spec
 * docs/specs/CAPTURE_BUILD_SPEC_V1_3.md; discovery
 * docs/audits/20260920_PHASE0_TICK_CAPTURE.md).
 *
 * TICK_CAPTURE_ENABLED — the evaluator mints `cronState.tickSeq` inside its
 * EXISTING admission transaction (the one permitted change to an existing
 * write, spec §4) and, as the LAST thing every admitted tick does, writes two
 * documents in ONE atomic batch: `agentBattles/{id}/ticks/{tickId}`
 * (permanent, no free text) and `agentBattles/{id}/tickBodies/{tickId}` (all
 * text, `expireAt = capturedAt + 120 days` for a Firestore TTL policy).
 *
 * Off → the admission transaction is BYTE-IDENTICAL to today, no sequence is
 * written, no subcollection document is written, the Anthropic client is
 * constructed with exactly today's options (no fetch observer), and every
 * existing write — the entry, the scores, the status feed, the final battle
 * update — is unchanged. Read as a module constant in
 * api/cron/agent-evaluate.js (the TICK_STAMPS_ENABLED / INTRADAY_DIAGNOSTIC
 * shape). The prompt sent to the model is identical on and off: capture never
 * touches a builder, a decision or a score.
 *
 * Capture is bounded and non-fatal: a bounded deadline
 * (TICK_CAPTURE_DEADLINE_MS) and a minimum remaining handler budget
 * (TICK_CAPTURE_MIN_REMAINING_BUDGET_MS), both in
 * api/_utils/tickCapture/captureConfig.js. A skipped, timed-out or failed
 * capture is a COUNTED gap and never alters the tick's own results or writes.
 *
 * FLIP PREREQUISITES (spec §6, and they are not part of the build that added
 * this flag): the Firestore TTL policy enabled on the `tickBodies` collection
 * group, the single-field index exemptions deployed, measured per-tick
 * overhead and record sizes within the §C-9 bound, and coverage reporting
 * working.
 *
 * NOW LIT — flipping starts the collection window (spec §6). FLIP MAP (the
 * flip reconciles these in the SAME commit — BUILD_RULES §2):
 *   • src/config/tickCaptureFlags.test.js — the dark pin row moves to true,
 *     and the registration row turns around to assert the entry is GONE;
 *   • src/config/flagPinGuard.test.js — drop TICK_CAPTURE_ENABLED from
 *     DARK_BY_DESIGN (its integrity test reds if a lit flag is left listed).
 * FALSE is now the ROLLBACK, one literal on the export below: with it false
 * the admission transaction is byte-identical to today and no subcollection
 * document is written. The flag-off byte-identity suite
 * (agent-evaluate.tickCapture.flagOff.test.js) and the legacy fixture suites
 * mock this flag to an explicit false and do NOT move with the flip; the
 * flag-on suites mock it true and do not move either.
 */
// Pinned by: tickCaptureFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const TICK_CAPTURE_ENABLED = true;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EVAL-CRON INSTRUMENTATION — the deferred beat (build report
 * docs/audits/20260923_BUILD_EVAL_DEFERRED_BEAT.md; discovery
 * docs/audits/20260918_PHASE0_EVAL_CRON_SCALING.md §1.4, re-confirmed by
 * docs/audits/20260923_PHASE0_COCKPIT.md §6.4).
 *
 * EVAL_DEFERRED_BEAT_ENABLED — when the evaluation loop in
 * api/cron/agent-evaluate.js runs out of its time budget, each battle it did
 * not reach gets ONE status-feed entry, `{ kind: 'check_deferred', at,
 * reason: 'budget', runId }`, appended with arrayUnion (no read, no lock);
 * the tape renders it as a `Check deferred` line, and the peek strip and the
 * character bubble mirror that line. At most DEFERRED_BEAT_CAP (25) per run,
 * and none unless 5 s of the function's hard ceiling remain. BEST EFFORT: an
 * overlapping invocation's whole-array feed rewrite can drop a beat; the run
 * document is the record. Off → no status-feed write of any kind; the tape has
 * nothing to render.
 *
 * NOT behind this flag, and always on: the per-run measurement document
 * agentEvalRuns/{runId} (it lists up to 200 deferred battles, beat or not, and
 * counts the rest), the `lockSkipped` / `deferred` split of the response's
 * `skipped`, and `tickMs` on every evaluation entry. Read as a module constant
 * inside the beat writer's fail-safe (the TICK_STAMPS_ENABLED shape).
 *
 * FLIP PREREQUISITES (not part of the build that added this flag; line
 * numbers as of that build — the build report §13 has the detail):
 *   • A COPY RULING. "next run picks it up first" is not what the scheduler
 *     guarantees: the rotation key advances only on a model attempt, so every
 *     battle reached without one — CPU seats, lock-skips, budget-skips,
 *     no-trigger ticks — sorts at or ahead of the deferred set next run; a
 *     battle deferred at the 15:45 ET run is completed, not checked, next;
 *     and at scale the next run may not reach it at all.
 *   • A RULING ON REPEATS. One beat per run per unreached battle (the spec):
 *     a battle deferred run after run collects identical, untimed lines —
 *     up to ~26 a day — which also crowd the short windows below.
 *   • The other status-feed readers key on `action` / `message` /
 *     `timestamp`, not `kind` / `at`. None throws, but:
 *       – the tournament Live feed renders a "—" row
 *         (src/components/Tournament/Flat6BattleView.jsx:335), and spectators
 *         receive the beat as `{}` through the public projection
 *         (api/_utils/tournamentBattleView.js:58, :96) — a fix keyed on
 *         `kind` must allowlist it there or drop beats at the projection;
 *       – the post-battle reflection prompt prints `[undefined] undefined:
 *         null`, every beat collapsing into that one line through the dedupe
 *         key while each still takes a last-10 slot
 *         (api/_utils/agentReflectionUtils.js:220-229, :339);
 *       – the league voice lane counts beats inside its 6-line window before
 *         dropping them (src/components/League/battleArena/statusFeedToVoice.js:55);
 *       – the Desk's latest line takes a beat as the latest entry
 *         (src/adapters/baggerbombAdapter.js:270) — dark today.
 *
 * Registered DARK_BY_DESIGN in flagPinGuard.test.js. FLIP MAP (the flip
 * reconciles these in the SAME commit — BUILD_RULES §2): in
 * src/config/evalDeferredBeatFlags.test.js the value pin moves to true and the
 * registration row turns around to assert the DARK_BY_DESIGN entry is GONE;
 * the DARK_BY_DESIGN entry is dropped. Crons do not run on preview, so the
 * first beat is observable only in production after the founder's merge.
 */
// Pinned by: evalDeferredBeatFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const EVAL_DEFERRED_BEAT_ENABLED = false;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE COCKPIT — CALL RECORDS (spec docs/design/COCKPIT_SPEC_V1_3.md §2–§3;
 * contract docs/CALL_RECORD_FIELD_CONTRACT_V1_3.md; Build 0 report
 * docs/audits/20260924_BUILD0_CALL_RECORDS.md).
 *
 * CALL_RECORDS_MODE — a STRING TRI-STATE, resolved ONCE per evaluation check
 * by resolveCallRecordsMode() (api/_utils/callRecords/mode.js), carried in the
 * check's calls context and never re-read mid-check:
 *   'off'    — no `declarations` property in the evaluation tool
 *            schema; no admission reserve; no observation snapshot; no read or
 *            write of `declarations/`, `calls/`, `callObservations/` or
 *            `callSweepQueue/`; no `declarationsPhase` on any entry and no
 *            `declarationsPhase` / `callFlips` / `callsDiag` on `cronState`;
 *            the tick-capture record keeps its pre-build shape (version 1).
 *            Every battle write, prompt byte and capture document is
 *            byte-identical to the frozen pre-change fixture
 *            (api/cron/agent-evaluate.callRecords.offGolden.test.js).
 *   'shadow' the model may declare; the validated block is recorded
 *            (declarations record + calls + sweep-queue arm, one transaction
 *            after the evaluation commit), encounter flips and receipts run,
 *            capture carries `calls[]` references — and NOTHING is rendered:
 *            chat, tiles and the endpoint are unchanged.
 *   'on'     (shipped — the cockpit flip, Build 2a spec §10.3) Build 1/2
 *            surfaces read the records (the chat calls block, the answer
 *            endpoint, the cockpit tiles), for the allowlisted owners'
 *            battles only: every other battle resolves 'off' (mode.js).
 *
 * RUNWAY: 'off' → 'shadow' is its own one-line founder PR after this build
 * merges AND the `calls` composite index (firestore.indexes.json) is deployed —
 * never a build PR; the flip moves the pin row in
 * src/config/callRecordsFlags.test.js in the SAME commit (BUILD_RULES §2).
 * Those two lines are the WHOLE flip: the seven cron suites that spread the
 * real flags and assert exact entry-key lists pin CALL_RECORDS_MODE: 'off' in
 * their own mocks (hermetic across the flip — the TICK_CAPTURE_ENABLED
 * precedent), and the Build 0 report records a dry run of exactly this flip
 * with the full suite green. The report also lists the rulings the shadow read
 * waits on (provenance origin, next_check horizons).
 * 'on' waits on Build 1 and Build 2 (spec §1). A rollback is the same line
 * back to 'off': existing records are then neither read nor written.
 *
 * Unknown values resolve to 'off' (fail closed). A string tri-state, so it is
 * pinned DIRECTLY — never a DARK_BY_DESIGN key (the flag-pin guard scans
 * `*_ENABLED = true|false` only; the ANTICIPATION_THRESHOLD_LINT_MODE
 * precedent).
 */
// Pinned by: callRecordsFlags.test.js (a STRING tri-state — pinned directly, outside flagPinGuard's `*_ENABLED` scan; this value and the pin move together — BUILD_RULES §2).
export const CALL_RECORDS_MODE = 'on';

/** The three founder-walked states, in walk order. */
export const CALL_RECORDS_MODES = Object.freeze(['off', 'shadow', 'on']);

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * COCKPIT BUILD 1a — THE ANSWER LOOP, dark (spec
 * docs/COCKPIT_BUILD1A_SPEC_V1_2.md §3; build report
 * docs/audits/20261002_BUILD1A_ANSWER_LOOP.md).
 *
 * THE PER-BATTLE ALLOWLIST IS NOT HERE ANY MORE (Cockpit Build 2a, spec
 * docs/COCKPIT_BUILD2A_SPEC_V1_0.md S-5; founder ruling R2A-7). This module
 * ships to every client, its namespace and source map included, so a uid
 * written here was public. The list is now a server-only environment
 * variable (named in api/_utils/callRecords/allowlist.js, never here), parsed
 * at call time by that module; the client asks
 * GET /api/agent/cockpit-status whether a battle is cockpit-on and never
 * learns who is admitted. No src/ module may import that reader
 * (allowlist.test.js pins it).
 */

/**
 * RESPONSE_FORK_ATTRIBUTION_ENABLED — the dormant response-fork hook (Build 1a
 * spec §11; Astra B1R2-9). Build 1a records PROMPT INCLUSION only: a `declared`
 * call event carries `promptDirectiveThreadId` when the check's committed
 * `heard` stamp names an unsuppressed thread — "Directive in this check's
 * prompt: …", never "In response to …". A CAUSAL label requires an explicit
 * per-fork field in the declarations block (a round-3 schema + nudge change)
 * and ships behind this flag. False: no causal field and no causal text,
 * anywhere.
 *
 * RUNWAY: flips only after the round-3 replay qualifies the attribution
 * protocol, in the founder's own one-line PR, never a build PR. The flip moves
 * the pin in cockpitFlags.test.js to true AND drops this flag from
 * DARK_BY_DESIGN in src/config/flagPinGuard.test.js in the same commit.
 */
// Pinned by: cockpitFlags.test.js, events.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const RESPONSE_FORK_ATTRIBUTION_ENABLED = false;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * COCKPIT BUILD 2a — THE COCKPIT SCREEN, built dark and lit by the founder's
 * flip PR (spec docs/COCKPIT_BUILD2A_SPEC_V1_0.md §10; build report
 * docs/audits/20261003_BUILD2A_COCKPIT_SCREEN.md).
 *
 * COCKPIT_UI_ENABLED — the screen flag. FALSE: every cockpit code path is
 * unreachable — no status request is sent, no calls / declarations / events
 * listener is opened, the desktop pane keeps Chat · Bench · Tape and the
 * phone layout is the shipped one, byte for byte.
 *
 * TRUE: the Battle View ASKS THE SERVER whether this battle is cockpit-on
 * (GET /api/agent/cockpit-status — the server resolves CALL_RECORDS_MODE and
 * its own allowlist; the client never recomputes the mode from bundled
 * constants). A battle is cockpit-on only when this flag is true AND the
 * server answered `{ on: true }`; unknown and errors read as off.
 *
 * Read at RENDER scope through isCockpitUiOn() below, never as a module-scope
 * const in a consumer (the isCharacterPaneOn rule — the featureFlags mock
 * hazard). The cockpit lives on the character pane's shells, so the accessor
 * also requires the pane.
 *
 * RUNWAY: flips only in the founder's own flip PR (spec §10.3) — one commit
 * with CALL_RECORDS_MODE = 'on', both pin updates and this flag's
 * DARK_BY_DESIGN removal — after the build merges, Amendment C is blessed and
 * the founder has set the server-only allowlist variable (allowlist.js names
 * it) to the founder's own uid in Vercel production. Never a build PR. The
 * flip moves the pin in cockpitUiFlags.test.js to true AND drops this flag
 * from DARK_BY_DESIGN in src/config/flagPinGuard.test.js in the same commit.
 */
// Pinned by: cockpitUiFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const COCKPIT_UI_ENABLED = true;

/** The cockpit screen gate, read at render: the pane's shells AND the screen flag. */
export function isCockpitUiOn() {
  return isCharacterPaneOn() && COCKPIT_UI_ENABLED;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * COCKPIT BUILD 2b — SPOTLIGHT, COMMANDS AND "WHO COMES IN?" (spec
 * docs/COCKPIT_BUILD2B_SPEC_V1_0.md S-11, §10; contract Amendment D,
 * docs/CALL_RECORD_FIELD_CONTRACT_V1_4_AMENDMENT_D.md).
 *
 * COCKPIT_2B_ENABLED — ON, default TRUE. When FALSE (the rollback) the
 * Build 2a screen renders as shipped plus the fixes the spec's §2 marks "from
 * merge" (the mint fields, the one swappable test, retire on exit, the one
 * expiry label, Following your call, one card per stock plan, Acted wins, the
 * tags, the server's clock for deadlines), and the answer endpoint
 * (api/agent/call-response.js) behaves as at HEAD for the budget, the pending
 * check and the answers — except the from-merge counterpart_unavailable check.
 *
 * TRUE: the endpoint's D-1 (7 commands, derived), D-2 (replace after one
 * confirmation) and D-4 (`replace`, `go_now` with a chosen name,
 * callActions.v2) behavior; the bench read (api/agent/cockpit-bench.js); and
 * the V5 screen — the Now card, "Who comes in?", Also live, the day line, the
 * commands meter, the bench sheet, board-row dots, the Research pill and the
 * chat composer line.
 *
 * Read at CALL TIME through isCockpit2bOn() below — the server reads it per
 * request, the client at render (never a module-scope const in a consumer, the
 * isCharacterPaneOn rule — the featureFlags vi.mock hazard). The server and the
 * client read this same accessor. No URL, environment or localStorage
 * override exists. The founder previews it on the never-merge smoke branch
 * (claude/cockpit-build2b-smoke), which flips this one line.
 *
 * RUNWAY: flips only in the founder's own flip PR (spec §10.2), merged after
 * 6 PM ET, after the build merges and the founder has smoked the preview.
 * Never a build PR. No environment change; the allowlist already holds the
 * founder and FT_QA. Rollback is this one line back to false (spec §10.3).
 * THE FLIP MAP — one commit (BUILD_RULES §2):
 *   1. this constant → true;
 *   2. cockpit2bFlags.test.js: the pin row → true and the registration row
 *      turned around to assert the DARK_BY_DESIGN entry is gone;
 *   3. src/config/flagPinGuard.test.js: this flag's DARK_BY_DESIGN entry dropped;
 *   4. this docstring: the state lines ("DARK, default FALSE", "When FALSE
 *      (the shipped state)") reworded to the live state.
 */
// Pinned by: cockpit2bFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const COCKPIT_2B_ENABLED = true;

/** The ONE seam for the 2b flag, read at call time by the server and at render by the client: the flag and nothing else. */
export function isCockpit2bOn() {
  return COCKPIT_2B_ENABLED;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * FILM ROOM BUILD A — THE TAPE (spec
 * docs/specs/FILM_ROOM_BUILD_A_TAPE_SPEC_V1_2_20260927.md; build report
 * docs/audits/20260927_BUILD_FILM_TAPE_A1.md).
 *
 * FILM_TAPE_WRITE_ENABLED — the writer flag. On, the close pass
 * (api/cron/film-tape-close.js, 22:15 ET) writes
 * `agentBattles/{id}/tape/{etDate}` for every battle-day, the candle pass
 * (api/cron/film-tape-candles.js, 07:00 ET) fills the replay, the plan prices
 * and `tape/{etDate}/series/{symbol}` from prior-session 1-minute bars, and the
 * admin backfill entry (`?backfill=YYYY-MM-DD..YYYY-MM-DD`) writes past days.
 * Read at CALL TIME inside each handler, before any read or write.
 *
 * Off → both handlers answer `{ skipped: true, reason: 'flag_off' }` with ZERO
 * Firestore reads and ZERO writes (the flag-off rows of
 * api/cron/film-tape-close.test.js and film-tape-candles.test.js prove it
 * against a recording store). The tape writes only its own subcollections
 * (BA-1) and never a body (BA-2), so no existing document changes either way;
 * with the screen flag below still off, a lit writer changes nothing a player
 * sees (spec §8 item 6, "Stage 2").
 *
 * FLIP (spec §10, its own founder PR, never a build PR): the flip PR runs the
 * BA-15 backfill from 2026-09-21 and smokes the first taped day. It moves the
 * pin in src/config/filmTapeFlags.test.js to true AND turns that file's
 * registration row around to assert the DARK_BY_DESIGN entry is GONE, and drops
 * the entry from src/config/flagPinGuard.test.js — all in the same commit
 * (BUILD_RULES §2). The tape's behaviour suites mock this flag explicitly both
 * ways and do NOT move with the flip. FLIP PREREQUISITES: the two `tape`
 * indexes declared in firestore.indexes.json — the collection-group composite
 * (passes.candles.status ASC, etDate ASC) the candle pass selects on, and the
 * `etDate` field override that adds DESCENDING at collection-group scope for
 * `export-film-tape --recent` — each ALSO created by hand in the Console (the
 * index-drift rule is dual-write; this build deploys neither); the two
 * `vercel.json` entries deployed; the rules block deployed.
 *
 * FLIPPED true 2026-10-01 — founder-cited (Flash, 2026-10-01), in its own flip
 * PR: the pin in filmTapeFlags.test.js moved to true, its registration row now
 * asserts the DARK_BY_DESIGN entry is gone, and the entry was dropped from
 * flagPinGuard.test.js, all in the flip commit. FILM_ROOM_V2_MODE below
 * gates the screen and the hub helper's Stage 3 (pinned in filmTapeFlags.test.js). ROLLBACK:
 * set this back to false (with the pin and the DARK_BY_DESIGN entry moving
 * back in the same commit) — both crons then answer `flag_off` again, and the
 * tapes already written stay where they are, unread.
 */
// Pinned by: filmTapeFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const FILM_TAPE_WRITE_ENABLED = true;

/**
 * FILM_ROOM_V2_MODE — the Film Room v2 screen gate (A2), a STRING TRI-STATE
 * that replaced the boolean FILM_ROOM_V2_ENABLED (Amendment E BA-40,
 * docs/specs/FILM_ROOM_BUILD_A_TAPE_SPEC_V1_2_AMENDMENT_E_20261008.md). It
 * resolves PER BATTLE OWNER (src/utils/filmRoomGate.js):
 *   'off'       — v2 resolves on for nobody: the 'filmRoom' route renders the
 *                 legacy FilmRoomScreen byte for byte (the golden captured at
 *                 the pre-build commit), and the hub helper
 *                 getReviewAvailability (src/utils/reviewAvailability.js,
 *                 spec §11) resolves Stage 1 from the battle document alone,
 *                 with no read.
 *   'allowlist' — v2 resolves on only for a battle whose owner the cockpit's
 *                 SERVER-SIDE allowlist admits, answered by the existing
 *                 GET /api/agent/cockpit-status verdict (its `allowlisted`
 *                 field) for the caller's own battle; the client holds no uid.
 *                 Everyone else gets the legacy screen and Stage 1.
 *   'on'        — v2 for every battle: FilmRoomScreenV2 at the same route and
 *                 entry points, and the hub helper's Stage 3 (one bounded read
 *                 of the final day's tape).
 * Unknown values resolve to 'off' (fail closed).
 *
 * RUNWAY: walked one founder PR per step, never a build PR. 'off' →
 * 'allowlist' was PR #950 (2026-10-10). 'allowlist' → 'on' is the CURRENT
 * value, its own PR (2026-10-11), after the follow-up pass (PR #952) and the
 * founder's ruling R15 (Amendment E addendum 6): v2 for every battle, with no
 * verdict request, so the allowlist's first-open wait of up to 8 s is gone,
 * and the hub helper's Stage 3 for every battle. That flip moved the pin row
 * in src/config/filmTapeFlags.test.js and turned around the hub-helper row of
 * api/cron/film-tape-flip.live.test.js — that file's tripwire for a screen
 * flip, which now asserts Stage 3 under the real flags — in the same commit
 * (BUILD_RULES §2). ROLLBACK: one line, this value back to 'allowlist', with
 * its pin row and the tripwire row moving back in the same commit. A
 * string tri-state, so it is pinned DIRECTLY — never a DARK_BY_DESIGN key (the
 * flag-pin guard scans `*_ENABLED = true|false` only; the CALL_RECORDS_MODE /
 * ANTICIPATION_THRESHOLD_LINT_MODE precedent) — and noted, by name, in the
 * DARK_BY_DESIGN block where a reader looks for the dark runway.
 */
// Pinned by: filmTapeFlags.test.js (a STRING tri-state — pinned directly, outside flagPinGuard's `*_ENABLED` scan; this value and the pin move together — BUILD_RULES §2).
export const FILM_ROOM_V2_MODE = 'on';

/** The three walked states, in walk order. */
export const FILM_ROOM_V2_MODES = Object.freeze(['off', 'allowlist', 'on']);

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SHADOW VERSUS CPU — QUOTE INTEGRITY (contract
 * SHADOW_CPU_PLACEHOLDER_PRICE_SPEC_V1_6.md, founder-authorized dark build;
 * build record docs/audits/20261002_SHADOW_CPU_QUOTE_INTEGRITY_BUILD_REVIEW.md).
 *
 * SHADOW_CPU_QUOTE_INTEGRITY_ENABLED — the ONE gate over the Shadow-vs-CPU
 * battle screen's quote-integrity path: value-specific quote provenance read
 * in the screen, an identified battle/position context, the discriminated
 * comparison every score consumer reads, lookup-evidence identity states, and
 * the controlled held-research view. DEFAULT false: the build merges dark.
 *
 * What is NOT behind this flag, on purpose: the PRODUCER metadata — the two
 * proxies' `quoteOrigin` and the stock fallback's `isFallback` marker
 * (api/stocks/prices.js, api/crypto/prices.js, src/services/eodhdAPI.js). It
 * ships live while the flag is dark so caches acquire it before any read
 * depends on it; every existing key, number, type, cache decision and request
 * stays byte-identical (OFF-2, OFF-3). The new optional props on the shared
 * components are inert unless the gated screen passes them.
 *
 * Flag OFF (shipped): the screen, its hooks, its consumers and the research
 * modal behave exactly as at the pre-build SHA 44d0c63e — pinned frame by
 * frame, request by request and digest by digest in the OFF suites the build
 * record lists. Flag ON: only admitted battles (explicit gameMode
 * 'baggerbomb_agent', opponent.odUserId 'cpu', no group stamp) take the gated
 * path; everything else is the legacy path after its own snapshot arrives.
 *
 * FLIP (R-11): its own founder PR, never a build PR, and NO EARLIER than the
 * first regular US stock-market open after the founder confirms the complete
 * additive metadata path is live in production (both proxies plus client
 * preservation and the fallback markers, flag still false) — record the
 * deployed version/time and the next actual America/New_York open. A preview
 * or a producer-PR merge alone is not that confirmation. No flush, TTL change
 * or warm-up request is authorized. The flip commit reconciles, together: this
 * value, the pin in src/config/shadowCpuQuoteIntegrityFlags.test.js, this
 * docstring's default prose, the `Pinned by:` pointer, and the DARK_BY_DESIGN
 * entry in src/config/flagPinGuard.test.js (BUILD_RULES §2). ROLLBACK is the
 * same reconciliation back to false; the additive metadata can remain, and the
 * legacy defect returns with it.
 *
 * NO OVERRIDE of any kind — no URL parameter, no localStorage key, no
 * environment variable. The accessor below is a plain constant read, unlike
 * isAgentPresenceOn (:1345); the pin suite asserts its source stays that way.
 *
 * Read it at RENDER scope through isShadowCpuQuoteIntegrityOn(), never as a
 * module-scope const (the Pass 1 featureFlags vi.mock hazard).
 */
// Pinned by: shadowCpuQuoteIntegrityFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const SHADOW_CPU_QUOTE_INTEGRITY_ENABLED = false;

/** The ONE consumer seam for the quote-integrity gate: the flag, and nothing else. */
export function isShadowCpuQuoteIntegrityOn() {
  return SHADOW_CPU_QUOTE_INTEGRITY_ENABLED;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PILOT P1a — VERSIONED HYPOTHESIS RECORDS, record-only (pilot spec
 * docs/specs/20260923_BAGGERBOMB_PARTNERSHIP_PILOT_SPEC_V1_4.md §2; Phase 0
 * docs/audits/20261005_PHASE0_PILOT_P1_P2_RECORDS.md; build report
 * docs/audits/20261007_BUILD_PILOT_P1A_HYPOTHESIS_RECORDS.md).
 *
 * HYPOTHESIS_RECORDS_ENABLED — the ONE gate over the player's versioned idea
 * records: `watchlists/{watchlistId}/hypothesisVersions/v{n}`, the parent
 * pointer (`currentHypothesisVersion`, `hypothesisVersionCount`), the owner
 * routes under api/forge/watchlists/[id]/hypothesis-*.js, the automatic v1 at
 * dialogue save, the review pass (the fifth tenant of
 * api/cron/process-pending-reflections.js) and the Forge "Idea" panel.
 * Pilot P2 (build report docs/audits/20261007_BUILD_PILOT_P2_RESEARCH_RECORDS.md)
 * puts the RESEARCH RECORDS behind the same switch: `researchWork/{id}`
 * minted and updated by the screener chat, the watchlist dialogue (and its
 * abandon route), the analysis sessions and the watchlist create/save paths;
 * the `researchWorkId` stamp on those hosts and on new lists; the research
 * refs on new versions; `mark_researched`; and the panel's research line.
 * Pilot P1b (build report docs/audits/20261009_BUILD_PILOT_P1B_DEPLOY_CARRIAGE.md;
 * founder ruling B1, spec Amendment D) puts DEPLOY CARRIAGE behind the same
 * switch, resolved for the battle's OWNER: a self-select deploy carries a
 * frozen copy of the equipped list's newest ready/activated version
 * (`agentContext.equippedHypothesis`), activates it and arms its review row in
 * the battle-creation transaction; the Forge's review lines name [SYM] or the
 * frozen [LIST]. Pilot P1c (founder ruling E1; build report
 * docs/audits/20261022_BUILD_PILOT_P1C_REVIEW_EXPERIENCE.md) replaced P1b's
 * refusal of a deploy whose idea is due for review with no ready version (the
 * 409 `hypothesis_review_due`): that deploy now carries nothing and says so on
 * its success response (`hypothesisNotice: 'review_due'`), and the deploy
 * station asks the player first (reaffirm, retire, or deploy without it).
 * P1c also puts, behind this same switch, the review card's evidence (GET
 * …/hypothesis-versions/[n]/review — the frozen list against SPY over the
 * idea's window, computed once and stored on the version) and the unwind of a
 * carrying battle that never ran (ruling E3) — by the review pass, or by the
 * next deploy when it reaches the version first.
 * Nothing the agent sees or trades reads any of it.
 *
 * TRUE is not enough on its own: the gate resolves ON only for owners on the
 * cockpit's server-side allowlist (the server-only variable that
 * api/_utils/callRecords/allowlist.js names and reads at call time —
 * api/_utils/hypothesisRecords/gate.js). Off for a caller: every new route
 * answers `404 { error: 'disabled' }`, the save path creates no version and
 * writes no pointer, the review pass returns before any read, and the panel
 * renders nothing (the client short-circuits on this constant before any
 * request, and otherwise shows nothing until the server admits the player).
 * LIVE since 2026-10-09: the slice merged dark (false) and the founder flipped
 * it true in its own PR (#948, the four edits below); with it false every
 * existing route, write and cron output is byte-identical to the pre-build
 * main.
 *
 * FLIP (done 2026-10-09, PR #948): its own founder PR, never a build PR, after the hypothesisVersions
 * collection-group index and the researchWork composite (firestore.indexes.json)
 * and the rules blocks are deployed (the P2 report's flip checklist covers both
 * builds). The flip is FOUR edits in one commit (BUILD_RULES §2; dry-run in the
 * P1b report §10): this line; the pin in src/config/hypothesisRecordsFlags.test.js
 * to true; that file's registration row turned around to assert the
 * DARK_BY_DESIGN entry is gone; and the entry dropped in
 * src/config/flagPinGuard.test.js. Rollback is the same line back to false:
 * nothing new is read or written; what was written stays — the versions
 * (still owner-readable under the rules, read by no app code) and the two
 * pointer fields on the lists that got one, which the existing owner GETs
 * return as part of the list document (review L3-6). P2 adds to what stays:
 * the research records (owner-readable, read by no app code once off; an open
 * one simply stays open) and the `researchWorkId` stamp on the sessions and
 * lists that got one (the screener-linked lists also keep their
 * `sourceSessionId`). P1b adds: battles already created keep their frozen
 * `equippedHypothesis` (read only by the call writer's provenance, the versions GET's frozen-list read, and the
 * owner's own battle reads); versions already activated stay `activated`
 * and their armed review rows stay in the queue — UNREVIEWED while off (the
 * review pass returns before any read), and judged on the next tick after a
 * re-flip. No new deploy carries, activates or refuses anything.
 *
 * Read it at RENDER/CALL scope through isHypothesisRecordsOn(), never as a
 * module-scope const in a consumer (the featureFlags vi.mock hazard).
 */
// Pinned by: hypothesisRecordsFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const HYPOTHESIS_RECORDS_ENABLED = true;

/** The ONE consumer seam for the hypothesis-records flag (the allowlist half is server-only). */
export function isHypothesisRecordsOn() {
  return HYPOTHESIS_RECORDS_ENABLED;
}

/**
 * DIALOGUE_SERVER_PARSE_ENABLED — Pilot P1c, THE PARSE BINDING (pilot spec
 * docs/specs/20260923_BAGGERBOMB_PARTNERSHIP_PILOT_SPEC_V1_4.md §2.8; founder
 * decision E4, 16 Oct 2026; build report
 * docs/audits/20261022_BUILD_PILOT_P1C_REVIEW_EXPERIENCE.md). §2.8 calls it
 * "a behavior change to existing dialogue output" that "ships as its own
 * founder-approved flip" — this flag is that flip.
 *
 * OFF: the watchlist dialogue's paste path
 * (api/forge/watchlist-dialogue.js, a first turn with `parseResult` +
 * `dropId`) stores the CLIENT's parse envelope as `session.parseResult` once
 * its contentHash matches the drop record, exactly as today; the dialogue
 * prompt is built from it. Byte-identical to the pre-build main (the off
 * golden: api/forge/watchlist-dialogue.parseBindingOffGolden.test.js).
 *
 * ON: the paste path loads the SERVER-stored parse for the dropId — the drop
 * record's own `parse` (users/{uid}/signalDrops/{dropId}; never the global
 * parse cache, which is keyed by content, not by drop — review L3-6) — and
 * stores THAT as `session.parseResult`; the hash check stays. An honest
 * client (its envelope equals the stored parse) gets a byte-identical
 * prompt; a client whose envelope differs gets the server's parse, and the
 * session records `parseEnvelopeMismatch: true` — never a failed request for
 * it. A drop with no usable stored parse is refused as a missing drop is
 * (400 unknown_drop). Theme sessions (no drop record) and continuing turns
 * are unchanged — so a session STARTED before the flip keeps feeding its
 * stored client envelope to its later turns (up to its 20 messages; review
 * L3-3). A URL-type drop keeps its URL on the drop record (`input.url`);
 * the dialogue never re-parses. (Spec §2.8's "text entry preserves an
 * entered source URL as attribution" is not built: the text entry drops a
 * URL typed beside the text before parse-signal — SignalDropEntry.jsx —
 * and this flag changes nothing there; review L3-5.) Gated on the flag
 * ALONE — no allowlist: the binding is a correctness fix for every player,
 * not a pilot surface.
 *
 * PROMPT-CONTRIBUTING (the parse feeds the dialogue prompt through
 * signalDropPrompt.js buildDialogueInputs → voiceLayerPrompt.js): its flip is
 * reviewed as one (call-record contract V1.4 §2's coordinated review, with a
 * prompt-bytes lens), though no fenced assembler is touched.
 *
 * FLIP: its own founder PR, never a build PR — FOUR edits in one commit
 * (BUILD_RULES §2; dry-run in the P1c report): this line; the pin in
 * src/config/dialogueServerParseFlags.test.js to true; that file's
 * registration row turned around to assert the DARK_BY_DESIGN entry is gone;
 * and the entry dropped in src/config/flagPinGuard.test.js (an
 * "intentionally ABSENT" note in its place). The same commit rewords the
 * state lines that name the shipped value (BUILD_RULES §2: "any docstring
 * that calls the old value the DEFAULT"): the pin file's header ("ships
 * FALSE") and its two row titles ("is DARK", "while it ships false"). Rollback is the
 * same four edits back: sessions already started keep the parseResult they
 * stored (and any parseEnvelopeMismatch marker); nothing else changes.
 *
 * The server reads the constant itself at call time (a test mocks it with a
 * getter); isDialogueServerParseOn() is the seam for anything else.
 */
// Pinned by: dialogueServerParseFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const DIALOGUE_SERVER_PARSE_ENABLED = true;

/** The ONE consumer seam for the parse-binding flag. */
export function isDialogueServerParseOn() {
  return DIALOGUE_SERVER_PARSE_ENABLED;
}

/**
 * PILOT_JOURNEY_MODE — the pilot's journey flag (spec §10.1), a STRING
 * TRI-STATE:
 *   'off'      — the pilot contributes nothing to any schema, prompt or store
 *                (contribution-relative, spec §6: only with every arc's flags
 *                off is the baseline byte-identical to today).
 *   'advisory' — isolated store only (spec §9.2): the harness exercises the
 *                pilot path against an injected namespace; zero ordinary live
 *                writes.
 *   'live'     — ordinary paths, allowlist-gated per mode (spec §9.5).
 * NOTHING READS IT. Founder ruling B1 (8 Oct 2026, spec Amendment D) amends
 * decision D3: P1b's deploy carriage is record-only and runs under
 * HYPOTHESIS_RECORDS_ENABLED + the cockpit allowlist, so this flag stays
 * reserved for what the agent consumes (P4) and the harness (P7) — its first
 * readers. Unknown values must resolve to 'off' at every future reader (fail
 * closed).
 *
 * RUNWAY: 'off' → 'advisory' → 'live', each its own founder PR after the
 * package that reads it lands, never a build PR; each step moves the pin row
 * in src/config/pilotJourneyModeFlags.test.js in the same commit (BUILD_RULES
 * §2). A string tri-state, so it is pinned DIRECTLY — never a DARK_BY_DESIGN
 * key (the flag-pin guard scans `*_ENABLED = true|false` only; the
 * CALL_RECORDS_MODE precedent).
 */
// Pinned by: pilotJourneyModeFlags.test.js (a STRING tri-state — pinned directly, outside flagPinGuard's `*_ENABLED` scan; this value and the pin move together — BUILD_RULES §2).
export const PILOT_JOURNEY_MODE = 'off';

/** The three spec states, in walk order. */
export const PILOT_JOURNEY_MODES = Object.freeze(['off', 'advisory', 'live']);

/**
 * SWAP_IDENTITY_MODE — Pilot P6, the swap identity check (G01; pilot spec
 * §7; Phase 0 docs/audits/20261007_PHASE0_PILOT_P6_SWAP_IDENTITY.md). A STRING
 * TRI-STATE read by the fenced executor (api/_utils/agentSwapExecution.js —
 * the default of its `identityMode` option, normalized by its exported
 * resolveSwapIdentityMode) and resolved ONCE per evaluation check by the cron,
 * which hands that resolution to every executor call it makes:
 *   'off'     — nothing is computed: every executor call is made with exactly
 *               the arguments it had before P6 (ten, or nine for a meeting
 *               leg), the executor's write and return value are
 *               byte-identical, no proposal or meeting leg stores an
 *               identity, and no refusal is recorded. Pinned by every
 *               existing golden (callRecords offGolden, the flag-off entry
 *               golden, tickCoherence noSwap, the guardrail pre-fix golden,
 *               the rollback golden) and the executor-level golden in
 *               api/_utils/agentSwapExecution.offGolden.test.js. Three
 *               honest-record fixes ship at every mode (the P6 build report).
 *   'shadow'  — inside the transaction the executor compares the slot's live
 *               occupant `{ symbol, swappedInAt }` with the caller's belief and
 *               reads the battle's status; the trade proceeds EXACTLY as today
 *               and its row carries `trades[i].verification`. The census.
 *   'enforce' — as shadow, but a mismatch or an inactive battle refuses the
 *               swap with a typed error (`reason: 'outgoing_identity_mismatch'
 *               | 'battle_not_active'`) and writes nothing; each caller holds
 *               and records the refusal (spec §7, founder decisions D2/D3).
 *
 * RUNWAY: 'off' ships with the P6 build PR. 'off' → 'shadow' is the founder's
 * own one-line PR after it merges — never a build PR. 'shadow' → 'enforce'
 * follows after at least five full trading sessions AND a shadow read
 * (scripts/census-swap-identity.mjs) — again one line. EACH step moves the pin
 * row in src/config/swapIdentityFlags.test.js in the SAME commit (BUILD_RULES
 * §2); a rollback is the same line back. Rolling back never rewrites a
 * recorded `verification` or refusal.
 *
 * Unknown values resolve to 'off' (fail closed). A string tri-state, so it is
 * pinned DIRECTLY — never a DARK_BY_DESIGN key (the flag-pin guard scans
 * `*_ENABLED = true|false` only; the CALL_RECORDS_MODE precedent).
 */
// Pinned by: swapIdentityFlags.test.js (a STRING tri-state — pinned directly, outside flagPinGuard's `*_ENABLED` scan; this value and the pin move together — BUILD_RULES §2).
export const SWAP_IDENTITY_MODE = 'enforce';

/** The three founder-walked states, in walk order. */
export const SWAP_IDENTITY_MODES = Object.freeze(['off', 'shadow', 'enforce']);

/**
 * CRYPTO_SLOT_PROMPT_ENABLED — the Crypto Slot paragraph (Fable build prompt,
 * 14 Oct 2026; Phase 0 docs/audits/20261014_PHASE0_CRYPTO_SLOT.md; build report
 * docs/audits/20261014_BUILD_CRYPTO_SLOT_PROMPT.md) and, since Crypto Slot v2
 * (build report docs/audits/20261014_BUILD_CRYPTO_SLOT_CHECK_LINE.md), the
 * per-check line and the RISK STATUS annotation — one flag for all three (D-9).
 * ON — default TRUE since the flip, 14 Oct 2026 (v2 build report §8, after
 * Fable's rulings in §10). Built dark.
 *
 * When TRUE (CURRENT):
 *   - the tiered (BaggerBomb agent game) eval system prompt carries a
 *     `━━━ YOUR CRYPTO SLOT ━━━` block (paragraph v2) between SCORING RULES
 *     and DECISION FRAMEWORK (api/_utils/evalCryptoSlotBlock.js, spliced once
 *     into the fenced agentEvalPromptAssembly.js);
 *   - the cron computes the CRYPTO SLOT THIS CHECK verdict — the held crypto
 *     for the bench crypto, through the model-swap gates' own functions
 *     (api/_utils/cryptoSlotVerdict.js, one call in the fenced
 *     agent-evaluate.js) — and the tiered live context shows it as one line
 *     immediately before RISK STATUS; when the verdict is "you cannot swap",
 *     the held crypto's SWAP_OUT line in RISK STATUS gains
 *     "— you cannot swap it this check; hold".
 * Prompt-only: no validator, executor, risk-manager, scorer or tool-schema
 * change. The flat6 (tournament) template and live context never carry any of
 * it — the tournament has no crypto.
 *
 * When FALSE (the rollback), the renders return '' / null, the cron attaches no
 * verdict to momentumData, and every eval system prompt and live context is
 * byte-identical to the pre-build text (evalCryptoSlotBlock.test.js against
 * __fixtures__/cryptoSlotPromptGoldens.json and cryptoSlotLiveContextGoldens.json,
 * both captured from the clean tree).
 *
 * Read it at CALL time, never module scope (the Ask 3 compileBuild lesson).
 * Flipped in its own commit, never in a build PR, after Fable ruled probe
 * v2's one missed clause non-blocking — the text flips exactly as probed
 * (v2 build report §10, ruling 1) — and Flash signed off on the §3 hunks.
 * THE FLIP MAP (v2 build report §8, which supersedes the #961 report's §7);
 * a rollback runs it in reverse: the flag → false; the pin in
 * cryptoSlotPromptFlags.test.js → false and this flag's DARK_BY_DESIGN entry
 * restored in flagPinGuard.test.js, in the same commit, with the state lines
 * here and the pin file's header reworded back (BUILD_RULES §2); the same
 * 7 snapshots regenerate — `npx vitest run
 * api/_utils/p4Equivalence.battery.test.js
 * api/_utils/evalIdentityBlock.injection.test.js -u` moves the tiered P4
 * analyst snapshot and the six tiered DR-13 identityOn snapshots (the flat6
 * ones must not move). Nothing else: six suites pin this flag false in their
 * own featureFlags mocks (they mock it, they do not pin it), so their goldens
 * stay the text of record and never regenerate —
 * agentEvalPromptAssembly.ask1.test.js, agentEvalPromptAssembly.ask2.test.js,
 * evalIdentityBlocks.ask2.test.js, agent-evaluate.callRecords.offGolden.test.js,
 * agent-evaluate.callRecords.rollback.test.js and
 * agent-evaluate.meetingCopy.baseline.test.js; evalCryptoSlotBlock.test.js,
 * cryptoSlotVerdict.test.js and agent-evaluate.cryptoSlotLine.test.js force
 * both states themselves.
 */
// Pinned by: cryptoSlotPromptFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const CRYPTO_SLOT_PROMPT_ENABLED = true;

// New battle selection only. Existing games dispatch from their saved tuple.
// v2 Ruling 6 and BR-4 (docs/approvals/BAGGERBOMB_STOCK_THRESHOLDS_V2_PILOT_20261015.md):
// ships true, and the server control document stockThresholdControl/admission
// is the only on-switch. An enabled schema-2 control selects either an
// allowlist of owners or, with exactly { mode: 'all' }, every owner's tiered
// full-day creation (never a tournament). With no such control, every creation
// is today's legacy creation with zero measurement reads.
// Rollback: disable the control document (no deploy needed).
// Pinned by: stockThresholdModel.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const STOCK_MEASURED_THRESHOLDS_ADMISSION_ENABLED = true;

/**
 * Command Center hub — slice S0 (spec
 * docs/specs/20261012_COMMAND_CENTER_CORE_IMPLEMENTATION_SPEC_V1_2.md §2, §4,
 * §11; build report docs/audits/20261015_BUILD_COMMAND_CENTER_HUB_S0.md).
 * LIVE — the shipped state is TRUE: the hub for every player since the
 * founder's flip PR of October 28, 2026
 * (docs/audits/20261028_ACTIVATION_COMMAND_CENTER_HUB.md).
 *
 * When TRUE, both dashboard mount sites in App.jsx render the hub
 * (CommandCenterHubGate, which lazily loads CommandCenterHub) in place of the
 * legacy shells. ONE pure picker makes the choice at both sites
 * (src/components/CommandCenter/pickDashboardShell.js) from four inputs: this
 * flag, isMobile, COMMAND_DASHBOARD_ENABLED and
 * COMMAND_DASHBOARD_DESKTOP_ENABLED. App keeps the host wrappers (the desktop
 * sidebar offset) and the starfield / background at both sites. If the hub
 * chunk fails to load, the gate renders the legacy shell this flag's FALSE
 * state would render at the same width and flags, under a Retry / Reload /
 * Dismiss notice.
 *
 * When FALSE (the rollback state): the picker returns the legacy shell at both
 * sites — CommandDashboard or DashboardLoop on mobile, CommandDashboardDesktop
 * (in its sidebar offset) or DashboardDesktop on desktop, by their own flags,
 * exactly as before S0. The gate is never rendered, so the hub chunk is never
 * requested and no hub read, timer, listener, subscription or effect exists.
 *
 * S0 was a scaffold (one labelled placeholder); the later slices (S1–S8, S2b,
 * the S8 fix build) built the hub behind this same flag.
 *
 * Read it at RENDER scope through isCommandCenterHubOn() below, never as a
 * module-scope const in a consumer (the isCharacterPaneOn rule — the
 * featureFlags vi.mock hazard). No URL, environment or localStorage override
 * exists. Before activation the founder previewed it on
 * never-merge smoke branches that flipped this one line and nothing else
 * (S0's is named in its build report).
 *
 * RUNWAY: activation was a separate founder action after the reviewed core,
 * deployment validation, fidelity/device evidence and useful Research for the
 * intended admitted cohort (spec §0, §12). Never a build PR. It happened on
 * October 28, 2026, in the founder's own flip PR, which ran the flip map below
 * in one commit; the activation record is
 * docs/audits/20261028_ACTIVATION_COMMAND_CENTER_HUB.md.
 * ROLLBACK: revert that one commit (or promote the previous deployment in
 * Vercel). The legacy dashboards stay in the code and return as soon as this
 * flag is false. The rollback state stays tested: App.bootOrder.jsdom.test.jsx
 * and App.activeBattlesPoll.owner.jsdom.test.jsx mock the accessor per row,
 * false for their legacy-shell rows and true for each row's hub sibling.
 * THE FLIP MAP — one commit, which a rollback runs in reverse (BUILD_RULES §2):
 *   1. this constant → true;
 *   2. commandCenterHubFlags.test.js: the pin row → true, the accessor row's
 *      title, and the registration row turned around to assert the
 *      DARK_BY_DESIGN entry is gone;
 *   3. src/config/flagPinGuard.test.js: this flag's DARK_BY_DESIGN entry dropped;
 *   4. this docstring: the state lines ("DARK — default FALSE", "When FALSE
 *      (the shipped state)") reworded to the live state.
 */
// Pinned by: commandCenterHubFlags.test.js (flagPinGuard: this value and the pin move together — BUILD_RULES §2).
export const COMMAND_CENTER_HUB_ENABLED = true;

/** The ONE consumer seam for the hub flag, read at render: the flag and nothing else. */
export function isCommandCenterHubOn() {
  return COMMAND_CENTER_HUB_ENABLED;
}

/**
 * FILM_ROOM_CARDS_MODE — the Film Room's take-home CARD (Build B spec
 * docs/specs/FILM_ROOM_BUILD_B_CARD_SPEC_V1_20261010.md §7; Amendment G
 * RG-2, RG-10; build report docs/audits/20261021_BUILD_FILM_ROOM_B2_CARD.md),
 * a STRING TRI-STATE after the FILM_ROOM_V2_MODE pattern. It resolves
 * PER BATTLE OWNER (src/utils/filmRoomGate.js on the client;
 * api/_utils/filmRoom/cardsGate.js on the server):
 *   'off'       — cards for nobody: the v2 screen's four reserved slots stay
 *                 empty and the screen is the CURRENT v2 byte for byte (the
 *                 golden captured at this build's base with FILM_ROOM_V2_MODE
 *                 'on'); no tag, moment or card renders anywhere; both
 *                 endpoints (POST /api/film-room/card, GET /api/film-room/counter)
 *                 answer 404 { error: 'disabled' } before any read.
 *   'allowlist' — cards only for a battle whose owner the cockpit's
 *                 SERVER-SIDE allowlist admits: the client reads the existing
 *                 GET /api/agent/cockpit-status `allowlisted` verdict for the
 *                 caller's own battle (the same cached verdict v2 used), the
 *                 server reads the allowlist at call time; the client holds no
 *                 uid. Everyone else: exactly 'off'.
 *   'on'        — cards for every owner.
 * Unknown values resolve to 'off' (fail closed). An untaped battle (RB-5)
 * opens the legacy screen in every mode and is refused by the card endpoint.
 * NOTHING THE AGENT SEES OR TRADES READS ANY OF IT: no model path imports the
 * ledger or reads `plays` (api/_utils/filmRoom/oneDoor.census.test.js), and
 * the assembled trading prompt is byte-identical with entries at `card` and
 * `nominated` and with an empty ledger (acceptance C).
 *
 * CURRENT: 'allowlist' (2026-10-29, the founder's flip PR #1013; it was 'off',
 * pinned dark, from B2 on 2026-10-21): cards for the owners the cockpit's
 * server-side allowlist admits (the founder and FT_QA); everyone else is the
 * 'off' screen as it was. RUNWAY: 'off' → 'allowlist' → 'on', each its own
 * founder PR, never a build PR, after the `plays` rules and the
 * `tape (ownerId, etDate)` collection-group index are deployed (Amendment G
 * RG-7; the build report gives the commands). Each step moves the pin row in
 * src/config/filmRoomCardsFlags.test.js in the same commit (BUILD_RULES §2).
 * The activation record is docs/audits/20261029_ACTIVATION_FILM_ROOM_CARDS_ALLOWLIST.md.
 * ROLLBACK: one line, this value back a step — from 'allowlist' that is one
 * line back to 'off' — with its pin row. A string
 * tri-state, so it is pinned DIRECTLY — never a DARK_BY_DESIGN key (the
 * flag-pin guard scans `*_ENABLED = true|false` only; the FILM_ROOM_V2_MODE /
 * CALL_RECORDS_MODE precedent) — and noted, by name, in the DARK_BY_DESIGN
 * block where a reader looks for the dark runway.
 */
// Pinned by: filmRoomCardsFlags.test.js (a STRING tri-state — pinned directly, outside flagPinGuard's `*_ENABLED` scan; this value and the pin move together — BUILD_RULES §2).
export const FILM_ROOM_CARDS_MODE = 'allowlist';

/** The three walked states, in walk order. */
export const FILM_ROOM_CARDS_MODES = Object.freeze(['off', 'allowlist', 'on']);
